import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Avatar from '@mui/material/Avatar';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import TablePagination from '@mui/material/TablePagination';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import Badge from '@mui/material/Badge';
import Menu from '@mui/material/Menu';
import Checkbox from '@mui/material/Checkbox';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { styled } from '@mui/material/styles';
import { toast } from 'react-toastify';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  Search,
  X,
  Filter,
  FileText,
  FileDown,
  Plus,
  Eye,
  Edit as EditIcon,
  Trash2,
  HelpCircle,
  Activity,
  CheckCircle2,
  Archive,
  PauseCircle,
  User,
  Phone,
  Mail,
  Building2,
  Landmark,
  Gavel,
  Flag,
  Paperclip,
  MoreVertical,
  ArrowUpDown,
  CalendarDays
} from 'lucide-react';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import api from '../services/api';
import { useJob } from '../context/JobContext';

const StyledCard = styled(Card)(() => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden'
}));

const tribunaleFilterOptions = createFilterOptions();

const STATUS_META = {
  'Da definire': { color: '#0284c7', icon: HelpCircle },
  'In corso': { color: '#3b82f6', icon: Activity },
  Definita: { color: '#16a34a', icon: CheckCircle2 },
  Archiviata: { color: '#64748b', icon: Archive },
  Sospesa: { color: '#f59e0b', icon: PauseCircle }
};

const emptyForm = {
  codice_pratica: '',
  stato_pratica: 'Da definire',
  stato_auto: true,
  tipo_atto_id: '',
  tipo_pratica_azione_id: '',
  tipi_posizione_credito_ids: [],
  cliente_id: '',
  controparte_id: '',
  creditore_principale_id: '',
  referente_legale_interno_id: '',
  referente_contabile_id: '',
  referente_legale_esterno_id: '',
  tribunale_id: '',
  oggetto: '',
  numero_rg: '',
  anno_rg: '',
  valore_pratica: '',
  contributo_unificato: '',
  priorita: 0,
  urgente: false,
  data_iscrizione_ruolo: '',
  termine_costituzione: '',
  termine_memorie: '',
  data_firma: '',
  data_apertura: '',
  data_scadenza: '',
  data_chiusura: '',
  note: ''
};

const emptyUdienzaForm = {
  data_udienza: '',
  ora_udienza: '',
  numero_udienza: '',
  esito_udienza_id: '',
  esito_libero: '',
  note: ''
};

const emptyNoteForm = {
  testo: '',
  tipo: 'operativa',
  visibilita: 'tutti_referenti',
  pinned: false,
  files: []
};

const formatDate = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('it-IT');
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('it-IT');
};

const isValidEmail = (value) => {
  const s = String(value || '').trim();
  if (!s) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
};

const formatRgValue = (numeroRg, annoRg) => {
  const numero = numeroRg ? String(numeroRg).trim() : '';
  const anno = annoRg ? String(annoRg).trim() : '';
  if (numero && anno) return `${numero}/${anno}`;
  if (numero) return numero;
  if (anno) return anno;
  return '-';
};

const isRgComplete = (numeroRg, annoRg) => {
  const numero = numeroRg ? String(numeroRg).trim() : '';
  const anno = annoRg ? String(annoRg).trim() : '';
  return Boolean(numero && anno);
};

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  const num = Number(value);
  if (!Number.isFinite(num)) return '-';
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(num);
};

const statusChipSx = (status) => {
  const color = STATUS_META[status]?.color || '#64748b';
  return {
    backgroundColor: `${color}14`,
    color,
    fontWeight: 600,
    border: `1px solid ${color}40`
  };
};

const getAutoStatusLabel = (value) => (Boolean(value) ? 'Stato: automatico' : 'Stato: manuale');

const getAutoStatusChipSx = (value) => {
  if (Boolean(value)) {
    return {
      backgroundColor: '#dcfce7',
      color: '#166534',
      border: '1px solid #86efac',
      fontWeight: 600
    };
  }
  return {
    backgroundColor: '#f1f5f9',
    color: '#334155',
    border: '1px solid #cbd5e1',
    fontWeight: 600
  };
};

const INITIAL_DOCS_ENTITY_TYPE = 'pratica_uo';
const INITIAL_DOCS_LEGACY_CATEGORY_CODE = 'pratiche_uo_documenti_iniziali';
const INITIAL_DOCS_CATEGORY_PREFIX = 'PRATICA_UO_ALLEGATO_';
const INITIAL_DOCS_ROLE = 'documento_iniziale';
const INITIAL_DOCS_MAX_BYTES = 10 * 1024 * 1024;
const INITIAL_DOCS_ALLOWED_MIME = ['application/pdf', 'image/jpeg', 'image/png'];
const NOTE_ENTITY_TYPE = 'pratica_uo_nota';
const NOTE_ALLEGATI_ROLE = 'allegato_nota';
const NOTE_ALLEGATI_CATEGORY_CODE = 'PRATICA_UO_NOTA';
const NOTE_MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;
const NOTE_TYPE_OPTIONS = [
  { value: 'operativa', label: 'Operativa' },
  { value: 'contabile', label: 'Contabile' },
  { value: 'legale', label: 'Legale' },
  { value: 'udienza', label: 'Udienza' },
  { value: 'scadenza', label: 'Scadenza' },
  { value: 'altro', label: 'Altro' }
];
const NOTE_VISIBILITY_OPTIONS = [
  { value: 'tutti_referenti', label: 'Tutti referenti' },
  { value: 'solo_interni', label: 'Interna (solo studio)' },
  { value: 'solo_contabile', label: 'Solo contabile' },
  { value: 'solo_legale', label: 'Solo legale' }
];

const formatBytes = (value) => {
  const bytes = Number(value || 0);
  if (!Number.isFinite(bytes) || bytes <= 0) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const noteTypeLabel = (value) =>
  NOTE_TYPE_OPTIONS.find((item) => item.value === value)?.label || 'Altro';

const noteVisibilityShortLabel = (value) => {
  if (value === 'solo_interni') return 'Interna';
  if (value === 'solo_contabile') return 'Contabile';
  if (value === 'solo_legale') return 'Legale';
  return 'Tutti';
};

const noteTypeColor = (value) => {
  if (value === 'contabile') return '#16a34a';
  if (value === 'legale') return '#2563eb';
  if (value === 'udienza') return '#7c3aed';
  if (value === 'scadenza') return '#f59e0b';
  if (value === 'altro') return '#64748b';
  return '#8b5cf6';
};

const getInitials = (name) => {
  const text = String(name || '').trim();
  if (!text) return 'UT';
  const parts = text.split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] || '';
  const second = parts[1]?.[0] || '';
  return `${first}${second || ''}`.toUpperCase();
};

const StatusStatWidget = ({ title, value, color, icon: Icon, active, onClick }) => (
  <Paper
    elevation={0}
    variant="outlined"
    sx={{
      p: 2,
      display: 'flex',
      alignItems: 'center',
      height: '100%',
      borderColor: active ? color : 'rgba(145, 158, 171, 0.24)',
      bgcolor: active ? `${color}08` : 'background.paper',
      cursor: 'pointer'
    }}
    onClick={onClick}
  >
    <Box
      sx={{
        p: 1.5,
        borderRadius: '50%',
        bgcolor: `${color}15`,
        color,
        mr: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <Icon size={20} />
    </Box>
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
        {title}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
        {value}
      </Typography>
    </Box>
  </Paper>
);

export default function PraticheUrgenzeOpposizioni() {
  const location = useLocation();
  const navigate = useNavigate();
  const { addJob } = useJob();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [search, setSearch] = useState('');
  const [showFilters, setShowFilters] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterUrgente, setFilterUrgente] = useState('');
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportIncludeDettaglio, setReportIncludeDettaglio] = useState(false);

  const [orderBy, setOrderBy] = useState('id');
  const [order, setOrder] = useState('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  const [statusOptions, setStatusOptions] = useState([
    'Da definire',
    'In corso',
    'Definita',
    'Archiviata',
    'Sospesa'
  ]);
  const [tribunaliOptions, setTribunaliOptions] = useState([]);
  const [tipiAttoOptions, setTipiAttoOptions] = useState([]);
  const [tipiPraticaAzioneOptions, setTipiPraticaAzioneOptions] = useState([]);
  const [tipiPosizioneCreditoOptions, setTipiPosizioneCreditoOptions] = useState([]);
  const [tipiAllegatoOptions, setTipiAllegatoOptions] = useState([]);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [issuerOptions, setIssuerOptions] = useState([]);
  const [referentiLegaliInterniOptions, setReferentiLegaliInterniOptions] = useState([]);
  const [referentiLegaliEsterniOptions, setReferentiLegaliEsterniOptions] = useState([]);
  const [referentiContabiliOptions, setReferentiContabiliOptions] = useState([]);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [issuerLoading, setIssuerLoading] = useState(false);
  const [customerInputValue, setCustomerInputValue] = useState('');
  const [issuerInputValue, setIssuerInputValue] = useState('');
  const [creditoreInputValue, setCreditoreInputValue] = useState('');
  const [customerLabelCache, setCustomerLabelCache] = useState({});
  const [issuerLabelCache, setIssuerLabelCache] = useState({});
  const [referentiInterniLabelCache, setReferentiInterniLabelCache] = useState({});
  const [referentiEsterniLabelCache, setReferentiEsterniLabelCache] = useState({});
  const [referentiContabiliLabelCache, setReferentiContabiliLabelCache] = useState({});
  const [statusStats, setStatusStats] = useState([]);
  const [tribunaleCreateDialogOpen, setTribunaleCreateDialogOpen] = useState(false);
  const [creatingTribunale, setCreatingTribunale] = useState(false);
  const [newTribunale, setNewTribunale] = useState({
    nome: '',
    citta: '',
    provincia: '',
    indirizzo: ''
  });

  const [columnMenuAnchorEl, setColumnMenuAnchorEl] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({
    oggetto: false,
    codice: false,
    id: false,
    cliente: true,
    controparte: true,
    posizioniCredito: false,
    creditorePrincipale: false,
    referenteLegaleInterno: true,
    referenteContabile: true,
    referenteLegaleEsterno: true,
    tribunale: true,
    annoRg: false,
    valorePratica: false,
    contributo: false,
    priorita: false,
    urgente: false,
    dataIscrizioneRuolo: false,
    termineCostituzione: false,
    termineMemorie: false,
    dataApertura: false,
    dataScadenza: true,
    dateUdienze: true,
    updatedAt: false
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailTarget, setEmailTarget] = useState(null);
  const [emailAccounts, setEmailAccounts] = useState([]);
  const [emailAccountsLoading, setEmailAccountsLoading] = useState(false);
  const [emailAccountId, setEmailAccountId] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailForm, setEmailForm] = useState({ to: '', cc: '', subject: '', body: '' });
  const [form, setForm] = useState(emptyForm);
  const [formTab, setFormTab] = useState(0);
  const [initialDocuments, setInitialDocuments] = useState([]);
  const [initialDocumentsLoading, setInitialDocumentsLoading] = useState(false);
  const [initialDocumentsUploading, setInitialDocumentsUploading] = useState(false);
  const [initialDocumentsDeletingLinkId, setInitialDocumentsDeletingLinkId] = useState(null);
  const [initialDocumentsDragActive, setInitialDocumentsDragActive] = useState(false);
  const [selectedInitialDocTipoCodice, setSelectedInitialDocTipoCodice] = useState('');
  const initialDocumentsInputRef = useRef(null);
  const [udienzeRows, setUdienzeRows] = useState([]);
  const [udienzeLoading, setUdienzeLoading] = useState(false);
  const [udienzeEsitiOptions, setUdienzeEsitiOptions] = useState([]);
  const [udienzaDialogOpen, setUdienzaDialogOpen] = useState(false);
  const [udienzaDeleteDialogOpen, setUdienzaDeleteDialogOpen] = useState(false);
  const [udienzaSaving, setUdienzaSaving] = useState(false);
  const [udienzaDeleteLoading, setUdienzaDeleteLoading] = useState(false);
  const [editingUdienza, setEditingUdienza] = useState(null);
  const [udienzaDeleteTarget, setUdienzaDeleteTarget] = useState(null);
  const [udienzaForm, setUdienzaForm] = useState(emptyUdienzaForm);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailRow, setDetailRow] = useState(null);
  const [detailUdienze, setDetailUdienze] = useState([]);
  const [noteRows, setNoteRows] = useState([]);
  const [noteLoading, setNoteLoading] = useState(false);
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteDeletingId, setNoteDeletingId] = useState(null);
  const [noteDeleteDialogOpen, setNoteDeleteDialogOpen] = useState(false);
  const [noteDeleteTarget, setNoteDeleteTarget] = useState(null);
  const [noteOrder, setNoteOrder] = useState('desc');
  const [noteForm, setNoteForm] = useState(emptyNoteForm);
  const [autoStatusInfoOpen, setAutoStatusInfoOpen] = useState(false);
  const noteFilesInputRef = useRef(null);
  const [udienzeDatesByPraticaId, setUdienzeDatesByPraticaId] = useState({});
  const [udienzeDatesLoadingByPraticaId, setUdienzeDatesLoadingByPraticaId] = useState({});
  const isUnmountedRef = useRef(false);
  const udienzeDatesInFlightRef = useRef(new Set());

  useEffect(() => {
    return () => {
      isUnmountedRef.current = true;
    };
  }, []);

  const maxFormTab = editingRow?.id ? 6 : 3;

  const activeFiltersCount = [filterStatus].filter(Boolean).length;
  const optionalColumnsActive = Object.values(columnVisibility).some(Boolean);

  const fetchMeta = async () => {
    try {
      const [metaRes, tribunaliRes, tipiAttoRes, tipiPraticaAzioneRes, esitiUdienzaRes, tipiAllegatoRes] = await Promise.all([
        api.get('/pratiche-urgenze-opposizioni/meta'),
        api.get('/tribunali'),
        api.get('/tipi-atto'),
        api.get('/tipi-pratica-azione'),
        api.get('/esiti-udienza'),
        api.get('/tipi-allegato-uo/active')
      ]);
      const stati = (metaRes.data?.statiPratica || []).filter(Boolean);
      if (stati.length > 0) {
        setStatusOptions(stati);
      }
      setTribunaliOptions(Array.isArray(tribunaliRes.data) ? tribunaliRes.data : []);
      setTipiAttoOptions(
        Array.isArray(tipiAttoRes.data) ? tipiAttoRes.data.filter((item) => item.attivo !== 0) : []
      );
      setTipiPraticaAzioneOptions(
        Array.isArray(tipiPraticaAzioneRes.data)
          ? tipiPraticaAzioneRes.data.filter((item) => item.attivo !== 0)
          : []
      );
      setTipiPosizioneCreditoOptions(
        Array.isArray(metaRes.data?.tipiPosizioneCredito)
          ? metaRes.data.tipiPosizioneCredito.filter((item) => item.attivo !== 0)
          : []
      );
      setUdienzeEsitiOptions(
        Array.isArray(esitiUdienzaRes.data)
          ? esitiUdienzaRes.data.filter((item) => item.attivo !== 0)
          : []
      );
      setTipiAllegatoOptions(Array.isArray(tipiAllegatoRes.data) ? tipiAllegatoRes.data : []);
      const referentiInterni = Array.isArray(metaRes.data?.referentiLegaliInterni)
        ? metaRes.data.referentiLegaliInterni
        : [];
      const referentiEsterni = Array.isArray(metaRes.data?.referentiLegaliEsterni)
        ? metaRes.data.referentiLegaliEsterni
        : [];
      const referentiContabili = Array.isArray(metaRes.data?.referentiContabili)
        ? metaRes.data.referentiContabili
        : [];
      setReferentiLegaliInterniOptions(referentiInterni);
      setReferentiLegaliEsterniOptions(referentiEsterni);
      setReferentiContabiliOptions(referentiContabili);
      setReferentiInterniLabelCache(
        referentiInterni.reduce((acc, item) => {
          acc[String(item.id)] = [item.name, item.email ? `(${item.email})` : '']
            .filter(Boolean)
            .join(' ');
          return acc;
        }, {})
      );
      setReferentiEsterniLabelCache(
        referentiEsterni.reduce((acc, item) => {
          acc[String(item.id)] = [item.nome, item.cognome].filter(Boolean).join(' ') || `ID ${item.id}`;
          return acc;
        }, {})
      );
      setReferentiContabiliLabelCache(
        referentiContabili.reduce((acc, item) => {
          acc[String(item.id)] = [item.name, item.email ? `(${item.email})` : '']
            .filter(Boolean)
            .join(' ');
          return acc;
        }, {})
      );
    } catch (error) {
      console.error('Error fetching pratiche U/O metadata:', error);
    }
  };

  const fetchEmailAccounts = async () => {
    setEmailAccountsLoading(true);
    try {
      const res = await api.get('/email-accounts/senders');
      const list = Array.isArray(res.data?.data) ? res.data.data : Array.isArray(res.data) ? res.data : [];
      setEmailAccounts(list);
      return list;
    } catch (_) {
      setEmailAccounts([]);
      return [];
    } finally {
      setEmailAccountsLoading(false);
    }
  };

  const buildDefaultEmailDraft = (row, udienzeDates = []) => {
    const customerName = String(row?.cliente_nome || '').trim() || (row?.cliente_id ? `Cliente #${row.cliente_id}` : 'Cliente');
    const customerEmail = String(row?.cliente_email || '').trim();
    const praticaCode = String(row?.codice_pratica || '').trim();
    const tipoLabel = row?.urgente ? 'Urgenza' : 'Opposizione';
    const tipoLower = row?.urgente ? 'urgenza' : 'opposizione';
    const controparteName = String(row?.controparte_nome || '').trim();
    const tribunaleNameRaw =
      String(row?.tribunale_nome || '').trim() ||
      (row?.tribunale_id ? String(tribunaleLabelById.get(String(row.tribunale_id)) || '').trim() : '');
    const tribunaleName = tribunaleNameRaw ? tribunaleNameRaw.split(' - ')[0].trim() : '';
    const rg = formatRgValue(row?.numero_rg, row?.anno_rg);
    const firstUdienza = Array.isArray(udienzeDates) && udienzeDates.length > 0 ? udienzeDates.slice().sort()[0] : '';

    const surnameCandidate = customerName
      .split(' ')
      .map((x) => x.trim())
      .filter(Boolean)
      .slice(-1)[0];
    const greetingSurname = surnameCandidate && !surnameCandidate.includes('@') ? surnameCandidate : customerName;

    const authorityArticle = tribunaleName.toLowerCase().startsWith('corte') ? 'la' : 'il';
    const authorityPhrase = tribunaleName ? `presso ${authorityArticle} ${tribunaleName}` : '';

    const subjectParts = [
      'Aggiornamento procedura',
      tipoLabel,
      praticaCode ? `- ${praticaCode}` : null
    ].filter(Boolean);

    const bodyLines = [
      `Gentile Sig. ${greetingSurname}`,
      '',
      `con la presente Le comunichiamo un aggiornamento in merito alla procedura di ${tipoLower}` +
        (controparteName ? ` promossa nei confronti di ${controparteName}` : '') +
        '.',
      '',
      authorityPhrase ? `La causa risulta iscritta ${authorityPhrase}` : 'La causa risulta iscritta.',
      rg && rg !== '-' ? `al n. R.G. ${rg}.` : null,
      '',
      firstUdienza ? `La prima udienza è stata fissata per il giorno ${formatDate(firstUdienza)}.` : null,
      '',
      'Provvederemo a monitorare costantemente l’andamento del procedimento e Le forniremo tempestivo aggiornamento su eventuali sviluppi o attività che richiedano il Suo intervento.',
      '',
      'Restiamo a disposizione per ogni chiarimento.',
      '',
      'Cordiali saluti',
      '',
      'Debito Bancario',
      '',
      '0899260613 / 3274425342'
    ].filter((x) => x !== null);

    return {
      to: isValidEmail(customerEmail) ? customerEmail : '',
      cc: '',
      subject: subjectParts.join(' '),
      body: bodyLines.join('<br />')
    };
  };

  const openEmailDialog = async (row) => {
    if (emailSending) return;
    let accounts = emailAccounts;
    if (accounts.length === 0) {
      accounts = await fetchEmailAccounts();
    }
    if (!Array.isArray(accounts) || accounts.length === 0) {
      toast.error('Nessuna casella email abilitata all’invio. Contatta un amministratore.');
      return;
    }

    setEmailTarget(row || null);
    const praticaId = row?.id ? String(row.id) : '';
    let udienzeDates = praticaId ? udienzeDatesByPraticaId[praticaId] : undefined;
    if (praticaId && udienzeDates === undefined && !udienzeDatesInFlightRef.current.has(praticaId)) {
      udienzeDatesInFlightRef.current.add(praticaId);
      try {
        const res = await api.get(`/pratiche-urgenze-opposizioni/${praticaId}/udienze`);
        const list = Array.isArray(res.data) ? res.data : Array.isArray(res.data?.data) ? res.data.data : [];
        udienzeDates = list
          .map((item) => (item?.data_udienza ? String(item.data_udienza).slice(0, 10) : ''))
          .filter(Boolean)
          .sort();
        setUdienzeDatesByPraticaId((prev) => ({ ...prev, [praticaId]: udienzeDates }));
      } catch (_) {
        udienzeDates = [];
        setUdienzeDatesByPraticaId((prev) => ({ ...prev, [praticaId]: [] }));
      } finally {
        udienzeDatesInFlightRef.current.delete(praticaId);
      }
    }
    const draft = buildDefaultEmailDraft(row, Array.isArray(udienzeDates) ? udienzeDates : []);
    setEmailForm(draft);
    setEmailDialogOpen(true);

    const selected = accounts[0] || null;
    setEmailAccountId(selected?.id != null ? String(selected.id) : '');

    if (!draft.to) {
      toast.warning('Email cliente non disponibile: compila manualmente il destinatario');
    }
  };

  const closeEmailDialog = () => {
    if (emailSending) return;
    setEmailDialogOpen(false);
    setEmailTarget(null);
  };

  const handleSendEmail = async () => {
    if (!emailAccountId) {
      toast.error('Seleziona un account email per l’invio');
      return;
    }
    if (!isValidEmail(emailForm.to) || !String(emailForm.subject || '').trim() || !String(emailForm.body || '').trim()) {
      toast.error('Compila i campi obbligatori (A, Oggetto, Messaggio)');
      return;
    }

    setEmailSending(true);
    try {
      const payload = new FormData();
      payload.append('to', String(emailForm.to).trim());
      if (emailForm.cc) {
        payload.append('cc', String(emailForm.cc).trim());
      }
      payload.append('subject', String(emailForm.subject).trim());
      payload.append('body', String(emailForm.body).replace(/<[^>]+>/g, ''));
      payload.append('body_html', String(emailForm.body));

      await api.post(`/email-accounts/${emailAccountId}/send`, payload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Email inviata con successo');
      closeEmailDialog();
    } catch (error) {
      const data = error?.response?.data || {};
      const backendMessage = data.message ? String(data.message) : '';
      const status = error?.response?.status;
      toast.error(backendMessage || (status ? `Errore ${status} durante l'invio dell'email` : "Errore durante l'invio dell'email"));
    } finally {
      setEmailSending(false);
    }
  };

  const formatCustomerLabel = (item) => {
    if (!item) return '';
    const fullName = [item.name, item.surname].filter(Boolean).join(' ').trim();
    return fullName || item.email || item.telephone || `ID ${item.id}`;
  };

  const formatIssuerLabel = (item) => {
    if (!item) return '';
    return item.name || item.direzione_generale || item.pec || `ID ${item.id}`;
  };

  const formatReferenteInternoLabel = (item) => {
    if (!item) return '';
    if (item.label) return item.label;
    return [item.name, item.email ? `(${item.email})` : ''].filter(Boolean).join(' ') || `ID ${item.id}`;
  };

  const formatReferenteEsternoLabel = (item) => {
    if (!item) return '';
    if (item.label) return item.label;
    return [item.nome, item.cognome].filter(Boolean).join(' ') || `ID ${item.id}`;
  };

  const renderContactPreview = ({ name, phone, email, fallback = '-' }) => (
    <Box sx={{ minWidth: 220, display: 'grid', gap: 0.25 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <User size={14} color="#2563eb" />
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {name || fallback}
        </Typography>
      </Box>
      {phone && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Phone size={13} color="#64748b" />
          <Typography variant="caption" color="text.secondary">
            {phone}
          </Typography>
        </Box>
      )}
      {email && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Mail size={13} color="#64748b" />
          <Typography variant="caption" color="text.secondary">
            {email}
          </Typography>
        </Box>
      )}
    </Box>
  );

  const fetchCustomerOptions = async (term = '') => {
    try {
      setCustomerLoading(true);
      const res = await api.get('/customer', {
        params: {
          context: 'pratiche',
          page: 1,
          limit: 25,
          orderBy: 'id',
          order: 'desc',
          search: term.trim()
        }
      });
      const list = Array.isArray(res.data?.data) ? res.data.data : [];
      setCustomerOptions(list);
      setCustomerLabelCache((prev) => {
        const next = { ...prev };
        list.forEach((item) => {
          next[String(item.id)] = formatCustomerLabel(item);
        });
        return next;
      });
    } catch (error) {
      console.error('Error fetching customer options:', error);
    } finally {
      setCustomerLoading(false);
    }
  };

  const fetchIssuerOptions = async (term = '') => {
    try {
      setIssuerLoading(true);
      const res = await api.get('/issuer', { params: { q: term.trim() } });
      const list = Array.isArray(res.data) ? res.data : [];
      setIssuerOptions(list);
      setIssuerLabelCache((prev) => {
        const next = { ...prev };
        list.forEach((item) => {
          next[String(item.id)] = formatIssuerLabel(item);
        });
        return next;
      });
    } catch (error) {
      console.error('Error fetching issuer options:', error);
    } finally {
      setIssuerLoading(false);
    }
  };

  const fetchCustomerById = async (id) => {
    if (!id) return null;
    try {
      const res = await api.get(`/customer/${id}`);
      return res.data || null;
    } catch (error) {
      console.error('Error fetching customer by id:', error);
      return null;
    }
  };

  const fetchIssuerById = async (id) => {
    if (!id) return null;
    try {
      const res = await api.get(`/issuer/${id}`);
      return res.data || null;
    } catch (error) {
      console.error('Error fetching issuer by id:', error);
      return null;
    }
  };

  const fetchRows = async () => {
    try {
      setLoading(true);
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        orderBy,
        order
      };
      if (search.trim()) params.search = search.trim();
      if (filterStatus) params.stato_pratica = filterStatus;
      if (filterUrgente === 'urgenze') params.urgente = 1;
      if (filterUrgente === 'opposizioni') params.urgente = 0;
      const res = await api.get('/pratiche-urgenze-opposizioni', { params });
      setRows(Array.isArray(res.data?.data) ? res.data.data : []);
      setTotal(Number(res.data?.total || 0));
    } catch (error) {
      console.error('Error fetching pratiche U/O:', error);
      toast.error('Errore nel caricamento delle pratiche U/O');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (filterUrgente === 'urgenze') params.urgente = 1;
      if (filterUrgente === 'opposizioni') params.urgente = 0;
      const res = await api.get('/pratiche-urgenze-opposizioni/stats/status', { params });
      setStatusStats(Array.isArray(res.data?.items) ? res.data.items : []);
    } catch (error) {
      console.error('Error fetching pratiche U/O status stats:', error);
    }
  };

  const handleStartPraticheUoReport = async () => {
    try {
      if (reportLoading) return;
      setReportLoading(true);
      const payload = {
        filters: {
          search: search.trim(),
          stato_pratica: filterStatus || '',
          urgente: filterUrgente === 'urgenze' ? 1 : filterUrgente === 'opposizioni' ? 0 : undefined
        },
        includeDettaglio: Boolean(reportIncludeDettaglio),
        orderBy,
        order
      };
      const res = await api.post('/reports/pratiche-uo', payload);
      const jobId = res?.data?.jobId;
      if (!jobId) throw new Error('jobId non ricevuto');
      addJob(jobId, 'pratiche_uo_report');
      setReportDialogOpen(false);
    } catch (error) {
      console.error('Errore avvio report pratiche U/O:', error);
      toast.error("Errore durante l'avvio del report pratiche U/O");
    } finally {
      setReportLoading(false);
    }
  };

  useEffect(() => {
    fetchMeta();
  }, []);

  useEffect(() => {
    fetchRows();
  }, [page, rowsPerPage, orderBy, order, search, filterStatus, filterUrgente]);

  useEffect(() => {
    fetchStats();
  }, [search, filterUrgente]);

  useEffect(() => {
    if (!Array.isArray(tipiAllegatoOptions) || tipiAllegatoOptions.length === 0) {
      setSelectedInitialDocTipoCodice('');
      return;
    }
    if (
      selectedInitialDocTipoCodice &&
      tipiAllegatoOptions.some((item) => item.codice === selectedInitialDocTipoCodice)
    ) {
      return;
    }
    setSelectedInitialDocTipoCodice(tipiAllegatoOptions[0].codice || '');
  }, [tipiAllegatoOptions, selectedInitialDocTipoCodice]);

  useEffect(() => {
    if (!dialogOpen) return;
    const timeoutId = setTimeout(() => {
      fetchCustomerOptions(customerInputValue || '');
    }, 350);
    return () => clearTimeout(timeoutId);
  }, [customerInputValue, dialogOpen]);

  useEffect(() => {
    if (!dialogOpen) return;
    const timeoutId = setTimeout(() => {
      fetchIssuerOptions(issuerInputValue || '');
    }, 350);
    return () => clearTimeout(timeoutId);
  }, [issuerInputValue, dialogOpen]);

  useEffect(() => {
    if (!dialogOpen) return;
    const timeoutId = setTimeout(() => {
      fetchIssuerOptions(creditoreInputValue || '');
    }, 350);
    return () => clearTimeout(timeoutId);
  }, [creditoreInputValue, dialogOpen]);

  useEffect(() => {
    if (!dialogOpen || formTab !== 6 || !editingRow?.id) return;
    fetchUdienze(editingRow.id);
  }, [dialogOpen, formTab, editingRow?.id]);

  useEffect(() => {
    if (!dialogOpen || formTab !== 5 || !editingRow?.id) return;
    fetchInitialDocuments(editingRow.id);
  }, [dialogOpen, formTab, editingRow?.id]);

  useEffect(() => {
    if (!dialogOpen || formTab !== 4 || !editingRow?.id) return;
    fetchNotes(editingRow.id, noteOrder);
  }, [dialogOpen, formTab, editingRow?.id, noteOrder]);

  useEffect(() => {
    const hydrateRowReferences = async () => {
      // Prime local label caches with names already returned by API rows,
      // so edit dialogs show readable labels immediately.
      setCustomerLabelCache((prev) => {
        const next = { ...prev };
        rows.forEach((r) => {
          if (!r?.cliente_id) return;
          const id = String(r.cliente_id);
          if (next[id]) return;
          const label = r.cliente_nome || '';
          if (label) next[id] = label;
        });
        return next;
      });

      setIssuerLabelCache((prev) => {
        const next = { ...prev };
        rows.forEach((r) => {
          if (r?.controparte_id) {
            const controparteId = String(r.controparte_id);
            if (!next[controparteId] && r.controparte_nome) {
              next[controparteId] = r.controparte_nome;
            }
          }
          if (r?.creditore_principale_id) {
            const creditoreId = String(r.creditore_principale_id);
            if (!next[creditoreId] && r.creditore_principale_nome) {
              next[creditoreId] = r.creditore_principale_nome;
            }
          }
        });
        return next;
      });

      setReferentiInterniLabelCache((prev) => {
        const next = { ...prev };
        rows.forEach((r) => {
          if (!r?.referente_legale_interno_id) return;
          const id = String(r.referente_legale_interno_id);
          if (next[id]) return;
          const label = r.referente_legale_interno_nome || '';
          if (label) next[id] = label;
        });
        return next;
      });

      setReferentiEsterniLabelCache((prev) => {
        const next = { ...prev };
        rows.forEach((r) => {
          if (!r?.referente_legale_esterno_id) return;
          const id = String(r.referente_legale_esterno_id);
          if (next[id]) return;
          const label = r.referente_legale_esterno_nome || '';
          if (label) next[id] = label;
        });
        return next;
      });

      setReferentiContabiliLabelCache((prev) => {
        const next = { ...prev };
        rows.forEach((r) => {
          if (!r?.referente_contabile_id) return;
          const id = String(r.referente_contabile_id);
          if (next[id]) return;
          const label = r.referente_contabile_nome || '';
          if (label) next[id] = label;
        });
        return next;
      });

      const customerIds = [...new Set(rows.map((r) => r.cliente_id).filter(Boolean))];
      const issuerIds = [
        ...new Set(
          rows
            .flatMap((r) => [r.controparte_id, r.creditore_principale_id])
            .filter(Boolean)
        )
      ];

      await Promise.all([
        Promise.all(
          customerIds.map(async (id) => {
            if (customerLabelCache[String(id)]) return;
            const item = await fetchCustomerById(id);
            if (item) {
              setCustomerLabelCache((prev) => ({ ...prev, [String(id)]: formatCustomerLabel(item) }));
            }
          })
        ),
        Promise.all(
          issuerIds.map(async (id) => {
            if (issuerLabelCache[String(id)]) return;
            const item = await fetchIssuerById(id);
            if (item) {
              setIssuerLabelCache((prev) => ({ ...prev, [String(id)]: formatIssuerLabel(item) }));
            }
          })
        )
      ]);
    };

    if (rows.length > 0) {
      hydrateRowReferences();
    }
  }, [rows]);

  const tribunaleLabelById = useMemo(() => {
    const map = new Map();
    tribunaliOptions.forEach((item) => {
      const label = [item.nome, item.citta].filter(Boolean).join(' - ');
      map.set(String(item.id), label || `ID ${item.id}`);
    });
    return map;
  }, [tribunaliOptions]);

  const selectedCustomerOption = useMemo(() => {
    const id = form.cliente_id ? String(form.cliente_id) : '';
    if (!id) return null;
    const found = customerOptions.find((item) => String(item.id) === id);
    if (found) return found;
    return {
      id: form.cliente_id,
      name: customerLabelCache[id] || `ID ${id}`
    };
  }, [form.cliente_id, customerOptions, customerLabelCache]);

  const selectedIssuerOption = useMemo(() => {
    const id = form.controparte_id ? String(form.controparte_id) : '';
    if (!id) return null;
    const found = issuerOptions.find((item) => String(item.id) === id);
    if (found) return found;
    return {
      id: form.controparte_id,
      name: issuerLabelCache[id] || `ID ${id}`
    };
  }, [form.controparte_id, issuerOptions, issuerLabelCache]);

  const selectedCreditoreOption = useMemo(() => {
    const id = form.creditore_principale_id ? String(form.creditore_principale_id) : '';
    if (!id) return null;
    const found = issuerOptions.find((item) => String(item.id) === id);
    if (found) return found;
    return {
      id: form.creditore_principale_id,
      name: issuerLabelCache[id] || `ID ${id}`
    };
  }, [form.creditore_principale_id, issuerOptions, issuerLabelCache]);

  const selectedReferenteInternoOption = useMemo(() => {
    const id = form.referente_legale_interno_id ? String(form.referente_legale_interno_id) : '';
    if (!id) return null;
    const found = referentiLegaliInterniOptions.find((item) => String(item.id) === id);
    if (found) return found;
    return {
      id: form.referente_legale_interno_id,
      label: referentiInterniLabelCache[id] || `ID ${id}`
    };
  }, [form.referente_legale_interno_id, referentiLegaliInterniOptions, referentiInterniLabelCache]);

  const selectedReferenteEsternoOption = useMemo(() => {
    const id = form.referente_legale_esterno_id ? String(form.referente_legale_esterno_id) : '';
    if (!id) return null;
    const found = referentiLegaliEsterniOptions.find((item) => String(item.id) === id);
    if (found) return found;
    return {
      id: form.referente_legale_esterno_id,
      label: referentiEsterniLabelCache[id] || `ID ${id}`
    };
  }, [form.referente_legale_esterno_id, referentiLegaliEsterniOptions, referentiEsterniLabelCache]);

  const selectedReferenteContabileOption = useMemo(() => {
    const id = form.referente_contabile_id ? String(form.referente_contabile_id) : '';
    if (!id) return null;
    const found = referentiContabiliOptions.find((item) => String(item.id) === id);
    if (found) return found;
    return {
      id: form.referente_contabile_id,
      label: referentiContabiliLabelCache[id] || `ID ${id}`
    };
  }, [form.referente_contabile_id, referentiContabiliOptions, referentiContabiliLabelCache]);

  const selectedTipiPosizioneCreditoOptions = useMemo(() => {
    const ids = Array.isArray(form.tipi_posizione_credito_ids)
      ? form.tipi_posizione_credito_ids.map((id) => String(id))
      : [];
    if (!ids.length) return [];

    return ids.map((id) => {
      const found = tipiPosizioneCreditoOptions.find((item) => String(item.id) === id);
      if (found) return found;
      return {
        id: Number(id),
        nome: `ID ${id}`,
        codice: `ID_${id}`
      };
    });
  }, [form.tipi_posizione_credito_ids, tipiPosizioneCreditoOptions]);

  const selectedTribunaleOption = useMemo(() => {
    const id = form.tribunale_id ? String(form.tribunale_id) : '';
    if (!id) return null;
    const found = tribunaliOptions.find((item) => String(item.id) === id);
    if (found) return found;
    return {
      id: form.tribunale_id,
      nome: tribunaleLabelById.get(id) || `ID ${id}`
    };
  }, [form.tribunale_id, tribunaliOptions, tribunaleLabelById]);

  const priorityMeta =
    form.priorita === 2
      ? { label: 'Alta', color: '#dc2626' }
      : form.priorita === 1
        ? { label: 'Media', color: '#d97706' }
        : { label: 'Bassa', color: '#2563eb' };

  const totalColumns =
    (columnVisibility.oggetto ? 1 : 0) +
    1 + // R.G.
    1 + // stato
    (columnVisibility.codice ? 1 : 0) +
    (columnVisibility.id ? 1 : 0) +
    (columnVisibility.cliente ? 1 : 0) +
    (columnVisibility.controparte ? 1 : 0) +
    (columnVisibility.posizioniCredito ? 1 : 0) +
    (columnVisibility.creditorePrincipale ? 1 : 0) +
    (columnVisibility.referenteLegaleInterno ? 1 : 0) +
    (columnVisibility.referenteContabile ? 1 : 0) +
    (columnVisibility.referenteLegaleEsterno ? 1 : 0) +
    (columnVisibility.tribunale ? 1 : 0) +
    (columnVisibility.annoRg ? 1 : 0) +
    (columnVisibility.valorePratica ? 1 : 0) +
    (columnVisibility.contributo ? 1 : 0) +
    (columnVisibility.priorita ? 1 : 0) +
    (columnVisibility.urgente ? 1 : 0) +
    (columnVisibility.dataIscrizioneRuolo ? 1 : 0) +
    (columnVisibility.termineCostituzione ? 1 : 0) +
    (columnVisibility.termineMemorie ? 1 : 0) +
    (columnVisibility.dataApertura ? 1 : 0) +
    (columnVisibility.dataScadenza ? 1 : 0) +
    (columnVisibility.dateUdienze ? 1 : 0) +
    (columnVisibility.updatedAt ? 1 : 0) +
    1; // azioni

  useEffect(() => {
    if (!columnVisibility.dateUdienze) return;
    if (!Array.isArray(rows) || rows.length === 0) return;

    const idsToFetch = rows
      .map((row) => (row?.id ? String(row.id) : ''))
      .filter(Boolean)
      .filter((id) => udienzeDatesByPraticaId[id] === undefined && !udienzeDatesInFlightRef.current.has(id));

    if (idsToFetch.length === 0) return;

    idsToFetch.forEach((id) => {
      udienzeDatesInFlightRef.current.add(id);
    });

    setUdienzeDatesLoadingByPraticaId((prev) => {
      const next = { ...prev };
      idsToFetch.forEach((id) => {
        next[id] = true;
      });
      return next;
    });

    (async () => {
      let results = [];
      try {
        results = await Promise.all(
          idsToFetch.map(async (id) => {
            try {
              const res = await api.get(`/pratiche-urgenze-opposizioni/${id}/udienze`);
              const list = Array.isArray(res.data) ? res.data : Array.isArray(res.data?.data) ? res.data.data : [];
              const dates = list
                .map((item) => (item?.data_udienza ? String(item.data_udienza).slice(0, 10) : ''))
                .filter(Boolean)
                .sort();
              return { id, dates };
            } catch (error) {
              return { id, dates: [] };
            }
          })
        );
      } finally {
        idsToFetch.forEach((id) => {
          udienzeDatesInFlightRef.current.delete(id);
        });
      }

      if (isUnmountedRef.current) return;

      setUdienzeDatesByPraticaId((prev) => {
        const next = { ...prev };
        results.forEach(({ id, dates }) => {
          next[id] = dates;
        });
        return next;
      });
      setUdienzeDatesLoadingByPraticaId((prev) => {
        const next = { ...prev };
        idsToFetch.forEach((id) => {
          delete next[id];
        });
        return next;
      });
    })();
  }, [rows, columnVisibility.dateUdienze]);

  const resetForm = () => {
    setForm(emptyForm);
  };

  const clearEditIdQueryParam = () => {
    const params = new URLSearchParams(location.search);
    if (!params.has('editId')) return;
    params.delete('editId');
    const query = params.toString();
    navigate(
      {
        pathname: location.pathname,
        search: query ? `?${query}` : ''
      },
      { replace: true }
    );
  };

  const closeFormDialog = () => {
    setDialogOpen(false);
    setFormTab(0);
    setEditingRow(null);
    setInitialDocuments([]);
    setInitialDocumentsDragActive(false);
    setInitialDocumentsLoading(false);
    setInitialDocumentsUploading(false);
    setInitialDocumentsDeletingLinkId(null);
    setNoteRows([]);
    setNoteLoading(false);
    setNoteSaving(false);
    setNoteDeletingId(null);
    setNoteDeleteDialogOpen(false);
    setNoteDeleteTarget(null);
    setNoteForm(emptyNoteForm);
    setNoteOrder('desc');
    setUdienzeRows([]);
    setUdienzaDialogOpen(false);
    setUdienzaDeleteDialogOpen(false);
    setEditingUdienza(null);
    setUdienzaDeleteTarget(null);
    resetUdienzaForm();
    resetForm();
    clearEditIdQueryParam();
  };

  const openCreateDialog = () => {
    setEditingRow(null);
    setInitialDocuments([]);
    setInitialDocumentsDragActive(false);
    setInitialDocumentsLoading(false);
    setInitialDocumentsUploading(false);
    setInitialDocumentsDeletingLinkId(null);
    setNoteRows([]);
    setNoteLoading(false);
    setNoteSaving(false);
    setNoteDeletingId(null);
    setNoteDeleteDialogOpen(false);
    setNoteDeleteTarget(null);
    setNoteForm(emptyNoteForm);
    setNoteOrder('desc');
    resetForm();
    setFormTab(0);
    setCustomerInputValue('');
    setIssuerInputValue('');
    setCreditoreInputValue('');
    setDialogOpen(true);
  };

  const openEditDialog = (row) => {
    // Ensure selected values render labels immediately in Autocomplete fields.
    if (row?.cliente_id && row?.cliente_nome) {
      setCustomerLabelCache((prev) => ({ ...prev, [String(row.cliente_id)]: row.cliente_nome }));
    }
    if (row?.controparte_id && row?.controparte_nome) {
      setIssuerLabelCache((prev) => ({ ...prev, [String(row.controparte_id)]: row.controparte_nome }));
    }
    if (row?.creditore_principale_id && row?.creditore_principale_nome) {
      setIssuerLabelCache((prev) => ({
        ...prev,
        [String(row.creditore_principale_id)]: row.creditore_principale_nome
      }));
    }
    if (row?.referente_legale_interno_id && row?.referente_legale_interno_nome) {
      setReferentiInterniLabelCache((prev) => ({
        ...prev,
        [String(row.referente_legale_interno_id)]: row.referente_legale_interno_nome
      }));
    }
    if (row?.referente_legale_esterno_id && row?.referente_legale_esterno_nome) {
      setReferentiEsterniLabelCache((prev) => ({
        ...prev,
        [String(row.referente_legale_esterno_id)]: row.referente_legale_esterno_nome
      }));
    }
    if (row?.referente_contabile_id && row?.referente_contabile_nome) {
      setReferentiContabiliLabelCache((prev) => ({
        ...prev,
        [String(row.referente_contabile_id)]: row.referente_contabile_nome
      }));
    }

    setEditingRow(row);
    setFormTab(0);
    setForm({
      codice_pratica: row.codice_pratica || '',
      stato_pratica: row.stato_pratica || 'Da definire',
      stato_auto: row.stato_auto === undefined || row.stato_auto === null ? true : Boolean(row.stato_auto),
      tipo_atto_id: row.tipo_atto_id ?? '',
      tipo_pratica_azione_id: row.tipo_pratica_azione_id ?? '',
      tipi_posizione_credito_ids: Array.isArray(row.tipi_posizione_credito_ids)
        ? row.tipi_posizione_credito_ids
        : [],
      cliente_id: row.cliente_id ?? '',
      controparte_id: row.controparte_id ?? '',
      creditore_principale_id: row.creditore_principale_id ?? '',
      referente_legale_interno_id: row.referente_legale_interno_id ?? '',
      referente_contabile_id: row.referente_contabile_id ?? '',
      referente_legale_esterno_id: row.referente_legale_esterno_id ?? '',
      tribunale_id: row.tribunale_id ?? '',
      oggetto: row.oggetto || '',
      numero_rg: row.numero_rg || '',
      anno_rg: row.anno_rg ?? '',
      valore_pratica: row.valore_pratica ?? '',
      contributo_unificato: row.contributo_unificato ?? '',
      priorita: Number(row.priorita ?? 0),
      urgente: Boolean(row.urgente),
      data_iscrizione_ruolo: row.data_iscrizione_ruolo ? String(row.data_iscrizione_ruolo).slice(0, 10) : '',
      termine_costituzione: row.termine_costituzione ? String(row.termine_costituzione).slice(0, 10) : '',
      termine_memorie: row.termine_memorie ? String(row.termine_memorie).slice(0, 10) : '',
      data_firma: row.data_firma ? String(row.data_firma).slice(0, 10) : '',
      data_apertura: row.data_apertura ? String(row.data_apertura).slice(0, 10) : '',
      data_scadenza: row.data_scadenza ? String(row.data_scadenza).slice(0, 10) : '',
      data_chiusura: row.data_chiusura ? String(row.data_chiusura).slice(0, 10) : '',
      note: row.note || ''
    });
    setCustomerInputValue('');
    setIssuerInputValue('');
    setCreditoreInputValue('');
    setDialogOpen(true);
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const editIdParam = params.get('editId');
    if (!editIdParam) return;

    const editId = Number(editIdParam);
    if (!Number.isInteger(editId) || editId <= 0) return;

    let cancelled = false;

    const openRequestedPratica = async () => {
      try {
        const res = await api.get(`/pratiche-urgenze-opposizioni/${editId}`);
        const pratica = res?.data;
        if (!pratica?.id || cancelled) return;
        openEditDialog(pratica);
      } catch (error) {
        console.error('Error loading pratica U/O by query param:', error);
        toast.error('Impossibile aprire la pratica richiesta');
      }
    };

    if (!dialogOpen || editingRow?.id !== editId) {
      openRequestedPratica();
    }

    return () => {
      cancelled = true;
    };
  }, [location.search]);

  const openDeleteDialog = (row) => {
    setDeleteTarget(row);
    setDeleteDialogOpen(true);
  };

  const openDetailDialog = async (row) => {
    setDetailRow(row);
    setDetailDialogOpen(true);
    setDetailLoading(true);
    try {
      const res = await api.get(`/pratiche-urgenze-opposizioni/${row.id}/udienze`);
      setDetailUdienze(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Error loading pratica U/O detail udienze:', error);
      setDetailUdienze([]);
      toast.error('Errore nel caricamento del dettaglio udienze');
    } finally {
      setDetailLoading(false);
    }
  };

  const fetchUdienze = async (praticaId) => {
    if (!praticaId) return;
    try {
      setUdienzeLoading(true);
      const res = await api.get(`/pratiche-urgenze-opposizioni/${praticaId}/udienze`);
      setUdienzeRows(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Error fetching udienze pratica U/O:', error);
      toast.error('Errore nel caricamento delle udienze');
    } finally {
      setUdienzeLoading(false);
    }
  };

  const fetchInitialDocuments = async (praticaId) => {
    if (!praticaId) return;
    try {
      setInitialDocumentsLoading(true);
      const res = await api.get(`/media/entity/${INITIAL_DOCS_ENTITY_TYPE}/${praticaId}`, {
        params: {
          role: INITIAL_DOCS_ROLE
        }
      });
      setInitialDocuments(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Error fetching initial documents for pratica U/O:', error);
      setInitialDocuments([]);
      toast.error('Errore nel caricamento dei documenti iniziali');
    } finally {
      setInitialDocumentsLoading(false);
    }
  };

  const validateInitialDocumentFiles = (files = []) => {
    if (!files.length) return [];
    const validFiles = [];
    for (const file of files) {
      const isValidMime = INITIAL_DOCS_ALLOWED_MIME.includes(file.type);
      if (!isValidMime) {
        toast.error(`Formato non supportato: ${file.name}. Ammessi: PDF, JPG, PNG`);
        continue;
      }
      if (file.size > INITIAL_DOCS_MAX_BYTES) {
        toast.error(`File troppo grande: ${file.name} (max 10MB)`);
        continue;
      }
      validFiles.push(file);
    }
    return validFiles;
  };

  const uploadInitialDocuments = async (files = []) => {
    if (!editingRow?.id) {
      toast.error('Salva prima la pratica per caricare i documenti iniziali');
      return;
    }
    const validFiles = validateInitialDocumentFiles(files);
    if (!validFiles.length) return;
    if (!selectedInitialDocTipoCodice) {
      toast.error('Seleziona una categoria allegato prima del caricamento');
      return;
    }

    try {
      setInitialDocumentsUploading(true);
      const formData = new FormData();
      validFiles.forEach((file) => formData.append('files', file));
      formData.append('category_code', `${INITIAL_DOCS_CATEGORY_PREFIX}${selectedInitialDocTipoCodice}`);
      formData.append('role', INITIAL_DOCS_ROLE);
      await api.post(`/media/entity/${INITIAL_DOCS_ENTITY_TYPE}/${editingRow.id}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Documenti iniziali caricati');
      await fetchInitialDocuments(editingRow.id);
    } catch (error) {
      console.error('Error uploading initial documents:', error);
      toast.error(error?.response?.data?.message || 'Errore nel caricamento dei documenti iniziali');
    } finally {
      setInitialDocumentsUploading(false);
    }
  };

  const resolveInitialDocumentCategoryLabel = (doc) => {
    const categoryCode = String(doc?.category_code || '').trim();
    if (!categoryCode) return 'Non classificato';
    if (categoryCode === INITIAL_DOCS_LEGACY_CATEGORY_CODE) return 'Storico (non classificato)';
    if (categoryCode.startsWith(INITIAL_DOCS_CATEGORY_PREFIX)) {
      const codice = categoryCode.slice(INITIAL_DOCS_CATEGORY_PREFIX.length);
      const match = tipiAllegatoOptions.find((item) => item.codice === codice);
      if (match?.nome) return match.nome;
    }
    if (doc?.category_name && doc.category_name !== categoryCode) return doc.category_name;
    return categoryCode;
  };

  const handleInitialDocumentsInputChange = async (event) => {
    const selectedFiles = Array.from(event.target?.files || []);
    if (!selectedFiles.length) return;
    await uploadInitialDocuments(selectedFiles);
    event.target.value = '';
  };

  const handleInitialDocumentsDrop = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    setInitialDocumentsDragActive(false);
    const droppedFiles = Array.from(event.dataTransfer?.files || []);
    if (!droppedFiles.length) return;
    await uploadInitialDocuments(droppedFiles);
  };

  const handleDeleteInitialDocument = async (linkId) => {
    if (!linkId || !editingRow?.id) return;
    try {
      setInitialDocumentsDeletingLinkId(linkId);
      await api.delete(`/media/link/${linkId}`);
      toast.success('Documento eliminato');
      await fetchInitialDocuments(editingRow.id);
    } catch (error) {
      console.error('Error deleting initial document:', error);
      toast.error(error?.response?.data?.message || 'Errore nell\'eliminazione del documento');
    } finally {
      setInitialDocumentsDeletingLinkId(null);
    }
  };

  const handleOpenInitialDocument = async (mediaId, fileName) => {
    if (!mediaId) return;
    try {
      const res = await api.get(`/media/${mediaId}/download`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: res.headers['content-type'] || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (error) {
      console.error('Error opening initial document:', error);
      toast.error(`Errore nell'apertura di ${fileName || 'documento'}`);
    }
  };

  const fetchNotes = async (praticaId, order = 'desc') => {
    if (!praticaId) return;
    try {
      setNoteLoading(true);
      const res = await api.get(`/pratiche-urgenze-opposizioni/${praticaId}/note`, {
        params: { order }
      });
      setNoteRows(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Error fetching note pratica U/O:', error);
      setNoteRows([]);
      toast.error('Errore nel caricamento note pratica');
    } finally {
      setNoteLoading(false);
    }
  };

  const handleNoteFileInputChange = (event) => {
    const selectedFiles = Array.from(event.target?.files || []);
    if (!selectedFiles.length) return;
    const validFiles = [];
    selectedFiles.forEach((file) => {
      if (file.size > NOTE_MAX_ATTACHMENT_BYTES) {
        toast.error(`File troppo grande: ${file.name} (max 10MB)`);
        return;
      }
      validFiles.push(file);
    });
    if (!validFiles.length) return;
    setNoteForm((prev) => ({ ...prev, files: [...prev.files, ...validFiles] }));
    event.target.value = '';
  };

  const handleRemovePendingNoteFile = (index) => {
    setNoteForm((prev) => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index)
    }));
  };

  const handleOpenNoteAttachment = async (mediaId, fileName) => {
    if (!mediaId) return;
    try {
      const res = await api.get(`/media/${mediaId}/download`, { responseType: 'blob' });
      const blob = new Blob([res.data], {
        type: res.headers['content-type'] || 'application/octet-stream'
      });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (error) {
      console.error('Error opening note attachment:', error);
      toast.error(`Errore nell'apertura di ${fileName || 'allegato nota'}`);
    }
  };

  const handleDeleteNoteAttachment = async (linkId) => {
    if (!linkId || !editingRow?.id) return;
    try {
      await api.delete(`/media/link/${linkId}`);
      toast.success('Allegato nota eliminato');
      await fetchNotes(editingRow.id, noteOrder);
    } catch (error) {
      console.error('Error deleting note attachment:', error);
      toast.error(error?.response?.data?.message || 'Errore nell\'eliminazione dell\'allegato');
    }
  };

  const handleSaveNote = async () => {
    if (!editingRow?.id) {
      toast.error('Salva prima la pratica per aggiungere note');
      return;
    }
    const testo = String(noteForm.testo || '').trim();
    if (!testo) {
      toast.error('Inserisci il testo della nota');
      return;
    }

    try {
      setNoteSaving(true);
      const createdRes = await api.post(`/pratiche-urgenze-opposizioni/${editingRow.id}/note`, {
        testo,
        tipo: noteForm.tipo,
        visibilita: noteForm.visibilita,
        pinned: Boolean(noteForm.pinned)
      });
      const createdNote = createdRes?.data;

      if (createdNote?.id && Array.isArray(noteForm.files) && noteForm.files.length > 0) {
        const formData = new FormData();
        noteForm.files.forEach((file) => formData.append('files', file));
        formData.append('category_code', NOTE_ALLEGATI_CATEGORY_CODE);
        formData.append('role', NOTE_ALLEGATI_ROLE);
        await api.post(`/media/entity/${NOTE_ENTITY_TYPE}/${createdNote.id}/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      toast.success('Nota salvata');
      setNoteForm(emptyNoteForm);
      await fetchNotes(editingRow.id, noteOrder);
    } catch (error) {
      console.error('Error saving note pratica U/O:', error);
      toast.error(error?.response?.data?.message || 'Errore nel salvataggio della nota');
    } finally {
      setNoteSaving(false);
    }
  };

  const handleTogglePinNote = async (row) => {
    if (!editingRow?.id || !row?.id) return;
    try {
      await api.put(`/pratiche-urgenze-opposizioni/${editingRow.id}/note/${row.id}`, {
        pinned: !Boolean(row.pinned)
      });
      await fetchNotes(editingRow.id, noteOrder);
    } catch (error) {
      console.error('Error toggling pin note pratica U/O:', error);
      toast.error(error?.response?.data?.message || 'Errore nell\'aggiornamento del pin nota');
    }
  };

  const handleRequestDeleteNote = (row) => {
    if (!row?.id) return;
    setNoteDeleteTarget(row);
    setNoteDeleteDialogOpen(true);
  };

  const handleDeleteNote = async () => {
    if (!editingRow?.id || !noteDeleteTarget?.id) return;
    try {
      setNoteDeletingId(noteDeleteTarget.id);
      await api.delete(
        `/pratiche-urgenze-opposizioni/${editingRow.id}/note/${noteDeleteTarget.id}`
      );
      toast.success('Nota eliminata');
      setNoteDeleteDialogOpen(false);
      setNoteDeleteTarget(null);
      await fetchNotes(editingRow.id, noteOrder);
    } catch (error) {
      console.error('Error deleting note pratica U/O:', error);
      toast.error(error?.response?.data?.message || 'Errore nell\'eliminazione della nota');
    } finally {
      setNoteDeletingId(null);
    }
  };

  const resetUdienzaForm = () => {
    setUdienzaForm(emptyUdienzaForm);
  };

  const openCreateUdienzaDialog = () => {
    setEditingUdienza(null);
    resetUdienzaForm();
    setUdienzaDialogOpen(true);
  };

  const openEditUdienzaDialog = (row) => {
    setEditingUdienza(row);
    setUdienzaForm({
      data_udienza: row.data_udienza ? String(row.data_udienza).slice(0, 10) : '',
      ora_udienza: row.ora_udienza ? String(row.ora_udienza).slice(0, 5) : '',
      numero_udienza: row.numero_udienza ?? '',
      esito_udienza_id: row.esito_udienza_id ?? '',
      esito_libero: row.esito_libero || '',
      note: row.note || ''
    });
    setUdienzaDialogOpen(true);
  };

  const openDeleteUdienzaDialog = (row) => {
    setUdienzaDeleteTarget(row);
    setUdienzaDeleteDialogOpen(true);
  };

  const handleSaveUdienza = async () => {
    if (!editingRow?.id) {
      toast.error('Salva prima la pratica per poter gestire le udienze');
      return;
    }
    if (!udienzaForm.data_udienza) {
      toast.error('La data udienza è obbligatoria');
      return;
    }
    try {
      setUdienzaSaving(true);
      const payload = {
        data_udienza: udienzaForm.data_udienza || null,
        ora_udienza: udienzaForm.ora_udienza || null,
        numero_udienza: udienzaForm.numero_udienza === '' ? null : Number(udienzaForm.numero_udienza),
        esito_udienza_id:
          udienzaForm.esito_udienza_id === '' ? null : Number(udienzaForm.esito_udienza_id),
        esito_libero: udienzaForm.esito_libero.trim() || null,
        note: udienzaForm.note.trim() || null
      };

      if (editingUdienza?.id) {
        await api.put(
          `/pratiche-urgenze-opposizioni/${editingRow.id}/udienze/${editingUdienza.id}`,
          payload
        );
        toast.success('Udienza aggiornata');
      } else {
        await api.post(`/pratiche-urgenze-opposizioni/${editingRow.id}/udienze`, payload);
        toast.success('Udienza aggiunta');
      }

      setUdienzaDialogOpen(false);
      setEditingUdienza(null);
      resetUdienzaForm();
      await fetchUdienze(editingRow.id);
    } catch (error) {
      console.error('Error saving udienza:', error);
      toast.error(error?.response?.data?.message || 'Errore nel salvataggio dell\'udienza');
    } finally {
      setUdienzaSaving(false);
    }
  };

  const handleDeleteUdienza = async () => {
    if (!editingRow?.id || !udienzaDeleteTarget?.id) return;
    try {
      setUdienzaDeleteLoading(true);
      await api.delete(
        `/pratiche-urgenze-opposizioni/${editingRow.id}/udienze/${udienzaDeleteTarget.id}`
      );
      toast.success('Udienza eliminata');
      setUdienzaDeleteDialogOpen(false);
      setUdienzaDeleteTarget(null);
      await fetchUdienze(editingRow.id);
    } catch (error) {
      console.error('Error deleting udienza:', error);
      toast.error(error?.response?.data?.message || 'Errore nell\'eliminazione dell\'udienza');
    } finally {
      setUdienzaDeleteLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const payload = {
        ...form,
        codice_pratica: form.codice_pratica.trim() || null,
        stato_auto: Boolean(form.stato_auto),
        tipo_atto_id: form.tipo_atto_id === '' ? null : Number(form.tipo_atto_id),
        tipo_pratica_azione_id:
          form.tipo_pratica_azione_id === '' ? null : Number(form.tipo_pratica_azione_id),
        tipi_posizione_credito_ids: Array.isArray(form.tipi_posizione_credito_ids)
          ? [...new Set(form.tipi_posizione_credito_ids.map((id) => Number(id)).filter((id) => Number.isInteger(id) && id > 0))]
          : [],
        cliente_id: form.cliente_id === '' ? null : Number(form.cliente_id),
        controparte_id: form.controparte_id === '' ? null : Number(form.controparte_id),
        creditore_principale_id:
          form.creditore_principale_id === '' ? null : Number(form.creditore_principale_id),
        referente_legale_interno_id:
          form.referente_legale_interno_id === '' ? null : Number(form.referente_legale_interno_id),
        referente_contabile_id:
          form.referente_contabile_id === '' ? null : Number(form.referente_contabile_id),
        referente_legale_esterno_id:
          form.referente_legale_esterno_id === '' ? null : Number(form.referente_legale_esterno_id),
        tribunale_id: form.tribunale_id === '' ? null : Number(form.tribunale_id),
        anno_rg: form.anno_rg === '' ? null : Number(form.anno_rg),
        valore_pratica: form.valore_pratica === '' ? null : Number(form.valore_pratica),
        contributo_unificato:
          form.contributo_unificato === '' ? null : Number(form.contributo_unificato),
        priorita: form.priorita === '' ? 0 : Number(form.priorita),
        urgente: Boolean(form.urgente),
        oggetto: form.oggetto.trim() || null,
        numero_rg: form.numero_rg.trim() || null,
        note: form.note.trim() || null
      };

      if (editingRow?.id) {
        await api.put(`/pratiche-urgenze-opposizioni/${editingRow.id}`, payload);
        toast.success('Pratica U/O aggiornata');
      } else {
        await api.post('/pratiche-urgenze-opposizioni', payload);
        toast.success('Pratica U/O creata');
      }

      closeFormDialog();
      await Promise.all([fetchRows(), fetchStats()]);
    } catch (error) {
      console.error('Error saving pratica U/O:', error);
      toast.error(error?.response?.data?.message || 'Errore nel salvataggio della pratica U/O');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget?.id) return;
    try {
      setDeleteLoading(true);
      await api.delete(`/pratiche-urgenze-opposizioni/${deleteTarget.id}`);
      toast.success('Pratica U/O eliminata');
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      await Promise.all([fetchRows(), fetchStats()]);
    } catch (error) {
      console.error('Error deleting pratica U/O:', error);
      toast.error(error?.response?.data?.message || 'Errore nell\'eliminazione della pratica U/O');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleToggleColumn = (key) => {
    setColumnVisibility((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const openCreateTribunaleDialog = (nomePrefill = '') => {
    setNewTribunale({
      nome: String(nomePrefill || '').trim(),
      citta: '',
      provincia: '',
      indirizzo: ''
    });
    setTribunaleCreateDialogOpen(true);
  };

  const handleCreateTribunale = async () => {
    const nome = String(newTribunale.nome || '').trim();
    if (!nome) {
      toast.error('Il nome del tribunale è obbligatorio');
      return;
    }

    try {
      setCreatingTribunale(true);
      const payload = {
        nome,
        citta: String(newTribunale.citta || '').trim() || null,
        provincia: String(newTribunale.provincia || '').trim().toUpperCase() || null,
        indirizzo: String(newTribunale.indirizzo || '').trim() || null
      };
      const res = await api.post('/tribunali', payload);
      const created = res?.data;
      if (!created?.id) {
        throw new Error('Risposta creazione tribunale non valida');
      }
      setTribunaliOptions((prev) => [...prev, created]);
      setForm((prev) => ({ ...prev, tribunale_id: Number(created.id) }));
      setTribunaleCreateDialogOpen(false);
      toast.success('Tribunale creato e selezionato');
    } catch (error) {
      console.error('Error creating tribunale from pratica form:', error);
      toast.error(error?.response?.data?.message || 'Errore nella creazione del tribunale');
    } finally {
      setCreatingTribunale(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#e0f2fe',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <FileText size={20} color="#0284c7" />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
              Pratiche Urgenze/Opposizioni
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Gestione procedimenti con atto ricevuto e azione legale
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button variant="outlined" startIcon={<FileDown size={18} />} onClick={() => setReportDialogOpen(true)}>
            Report
          </Button>
          <Button variant="contained" startIcon={<Plus size={18} />} onClick={openCreateDialog}>
            Nuova pratica U/O
          </Button>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        {(statusStats.length ? statusStats : statusOptions.map((status) => ({ status, count: 0 }))).map(
          (item) => {
            const status = item.status;
            const meta = STATUS_META[status] || STATUS_META['Da definire'];
            return (
              <Box key={status} sx={{ flex: '1 1 0', minWidth: 180 }}>
                <StatusStatWidget
                  title={status}
                  value={item.count || 0}
                  icon={meta.icon}
                  color={meta.color}
                  active={filterStatus === status}
                  onClick={() => {
                    setFilterStatus((prev) => (prev === status ? '' : status));
                    setPage(0);
                    setShowFilters(true);
                  }}
                />
              </Box>
            );
          }
        )}
      </Box>

      <StyledCard>
        <Grid container spacing={2} sx={{ mb: 3, p: 2 }}>
          <Grid item xs={12} md={showFilters ? 8 : 12}>
            <TextField
              fullWidth
              placeholder="Cerca per codice, oggetto, cliente, numero R.G., tribunale..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={20} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      {search && (
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSearch('');
                            setPage(0);
                          }}
                        >
                          <X size={18} />
                        </IconButton>
                      )}
                      <IconButton onClick={() => setShowFilters((prev) => !prev)}>
                        <Badge badgeContent={activeFiltersCount} color="primary">
                          <FilterAltIcon color={showFilters || activeFiltersCount > 0 ? 'primary' : 'action'} />
                        </Badge>
                      </IconButton>
                      <Tooltip title="Seleziona colonne aggiuntive">
                        <IconButton onClick={(e) => setColumnMenuAnchorEl(e.currentTarget)}>
                          <ViewColumnIcon color={optionalColumnsActive ? 'primary' : 'action'} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </InputAdornment>
                )
              }}
            />
          </Grid>

          {showFilters && (
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Stato pratica</InputLabel>
                <Select
                  value={filterStatus}
                  label="Stato pratica"
                  onChange={(e) => {
                    setFilterStatus(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="">
                    <em>Tutti</em>
                  </MenuItem>
                  {statusOptions.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          {showFilters && (
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Tipo</InputLabel>
                <Select
                  value={filterUrgente}
                  label="Tipo"
                  onChange={(e) => {
                    setFilterUrgente(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="">
                    <em>Tutti</em>
                  </MenuItem>
                  <MenuItem value="urgenze">Solo Urgenze</MenuItem>
                  <MenuItem value="opposizioni">Solo Opposizioni</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}
        </Grid>

        <Menu
          anchorEl={columnMenuAnchorEl}
          open={Boolean(columnMenuAnchorEl)}
          onClose={() => setColumnMenuAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Box sx={{ px: 2, pt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Colonne aggiuntive
            </Typography>
          </Box>
          <FormGroup sx={{ px: 2, pb: 1 }}>
            <FormControlLabel
              control={<Checkbox size="small" checked={columnVisibility.oggetto} onChange={() => handleToggleColumn('oggetto')} />}
              label="Oggetto"
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={columnVisibility.codice}
                  onChange={() => handleToggleColumn('codice')}
                />
              }
              label="Codice"
            />
            <FormControlLabel
              control={<Checkbox size="small" checked={columnVisibility.id} onChange={() => handleToggleColumn('id')} />}
              label="ID"
            />
            <FormControlLabel
              control={<Checkbox size="small" checked={columnVisibility.cliente} onChange={() => handleToggleColumn('cliente')} />}
              label="Cliente ID"
            />
            <FormControlLabel
              control={<Checkbox size="small" checked={columnVisibility.controparte} onChange={() => handleToggleColumn('controparte')} />}
              label="Controparte ID"
            />
            <FormControlLabel
              control={<Checkbox size="small" checked={columnVisibility.posizioniCredito} onChange={() => handleToggleColumn('posizioniCredito')} />}
              label="Posizioni credito"
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={columnVisibility.creditorePrincipale}
                  onChange={() => handleToggleColumn('creditorePrincipale')}
                />
              }
              label="Creditore principale"
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={columnVisibility.referenteLegaleInterno}
                  onChange={() => handleToggleColumn('referenteLegaleInterno')}
                />
              }
              label="Referente legale interno"
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={columnVisibility.referenteContabile}
                  onChange={() => handleToggleColumn('referenteContabile')}
                />
              }
              label="Referente contabile"
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={columnVisibility.referenteLegaleEsterno}
                  onChange={() => handleToggleColumn('referenteLegaleEsterno')}
                />
              }
              label="Referente legale esterno"
            />
            <FormControlLabel
              control={<Checkbox size="small" checked={columnVisibility.tribunale} onChange={() => handleToggleColumn('tribunale')} />}
              label="Tribunale"
            />
            <FormControlLabel
              control={<Checkbox size="small" checked={columnVisibility.annoRg} onChange={() => handleToggleColumn('annoRg')} />}
              label="Anno R.G."
            />
            <FormControlLabel
              control={<Checkbox size="small" checked={columnVisibility.valorePratica} onChange={() => handleToggleColumn('valorePratica')} />}
              label="Valore pratica"
            />
            <FormControlLabel
              control={<Checkbox size="small" checked={columnVisibility.contributo} onChange={() => handleToggleColumn('contributo')} />}
              label="Contributo"
            />
            <FormControlLabel
              control={<Checkbox size="small" checked={columnVisibility.priorita} onChange={() => handleToggleColumn('priorita')} />}
              label="Priorità"
            />
            <FormControlLabel
              control={<Checkbox size="small" checked={columnVisibility.urgente} onChange={() => handleToggleColumn('urgente')} />}
              label="Urgente"
            />
            <FormControlLabel
              control={<Checkbox size="small" checked={columnVisibility.dataIscrizioneRuolo} onChange={() => handleToggleColumn('dataIscrizioneRuolo')} />}
              label="Iscrizione a ruolo"
            />
            <FormControlLabel
              control={<Checkbox size="small" checked={columnVisibility.termineCostituzione} onChange={() => handleToggleColumn('termineCostituzione')} />}
              label="Termine costituzione"
            />
            <FormControlLabel
              control={<Checkbox size="small" checked={columnVisibility.termineMemorie} onChange={() => handleToggleColumn('termineMemorie')} />}
              label="Termine memorie"
            />
            <FormControlLabel
              control={<Checkbox size="small" checked={columnVisibility.dataApertura} onChange={() => handleToggleColumn('dataApertura')} />}
              label="Data apertura"
            />
            <FormControlLabel
              control={<Checkbox size="small" checked={columnVisibility.dataScadenza} onChange={() => handleToggleColumn('dataScadenza')} />}
              label="Data scadenza"
            />
            <FormControlLabel
              control={<Checkbox size="small" checked={columnVisibility.dateUdienze} onChange={() => handleToggleColumn('dateUdienze')} />}
              label="Date udienze"
            />
            <FormControlLabel
              control={<Checkbox size="small" checked={columnVisibility.updatedAt} onChange={() => handleToggleColumn('updatedAt')} />}
              label="Ultimo aggiornamento"
            />
          </FormGroup>
        </Menu>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sortDirection={orderBy === 'numero_rg' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'numero_rg'}
                    direction={orderBy === 'numero_rg' ? order : 'asc'}
                    onClick={() => handleRequestSort('numero_rg')}
                  >
                    R.G.
                  </TableSortLabel>
                </TableCell>
                {columnVisibility.codice && (
                  <TableCell sortDirection={orderBy === 'codice_pratica' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'codice_pratica'}
                      direction={orderBy === 'codice_pratica' ? order : 'asc'}
                      onClick={() => handleRequestSort('codice_pratica')}
                    >
                      Codice
                    </TableSortLabel>
                  </TableCell>
                )}
                {columnVisibility.oggetto && <TableCell>Oggetto</TableCell>}
                <TableCell sortDirection={orderBy === 'stato_pratica' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'stato_pratica'}
                    direction={orderBy === 'stato_pratica' ? order : 'asc'}
                    onClick={() => handleRequestSort('stato_pratica')}
                  >
                    Stato
                  </TableSortLabel>
                </TableCell>

                {columnVisibility.id && (
                  <TableCell sortDirection={orderBy === 'id' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'id'}
                      direction={orderBy === 'id' ? order : 'asc'}
                      onClick={() => handleRequestSort('id')}
                    >
                      ID
                    </TableSortLabel>
                  </TableCell>
                )}
                {columnVisibility.cliente && <TableCell>Cliente</TableCell>}
                {columnVisibility.controparte && <TableCell>Controparte</TableCell>}
                {columnVisibility.posizioniCredito && <TableCell>Posizioni credito</TableCell>}
                {columnVisibility.creditorePrincipale && (
                  <TableCell sortDirection={orderBy === 'creditore_principale_nome' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'creditore_principale_nome'}
                      direction={orderBy === 'creditore_principale_nome' ? order : 'asc'}
                      onClick={() => handleRequestSort('creditore_principale_nome')}
                    >
                      Creditore principale
                    </TableSortLabel>
                  </TableCell>
                )}
                {columnVisibility.referenteLegaleInterno && (
                  <TableCell sortDirection={orderBy === 'referente_legale_interno_nome' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'referente_legale_interno_nome'}
                      direction={orderBy === 'referente_legale_interno_nome' ? order : 'asc'}
                      onClick={() => handleRequestSort('referente_legale_interno_nome')}
                    >
                      <Box component="span" sx={{ display: 'inline-block', whiteSpace: 'normal', lineHeight: 1.2 }}>
                        Referente legale
                        <br />
                        interno
                      </Box>
                    </TableSortLabel>
                  </TableCell>
                )}
                {columnVisibility.referenteContabile && (
                  <TableCell sortDirection={orderBy === 'referente_contabile_nome' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'referente_contabile_nome'}
                      direction={orderBy === 'referente_contabile_nome' ? order : 'asc'}
                      onClick={() => handleRequestSort('referente_contabile_nome')}
                    >
                      <Box component="span" sx={{ display: 'inline-block', whiteSpace: 'normal', lineHeight: 1.2 }}>
                        Referente
                        <br />
                        contabile
                      </Box>
                    </TableSortLabel>
                  </TableCell>
                )}
                {columnVisibility.referenteLegaleEsterno && (
                  <TableCell sortDirection={orderBy === 'referente_legale_esterno_nome' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'referente_legale_esterno_nome'}
                      direction={orderBy === 'referente_legale_esterno_nome' ? order : 'asc'}
                      onClick={() => handleRequestSort('referente_legale_esterno_nome')}
                    >
                      <Box component="span" sx={{ display: 'inline-block', whiteSpace: 'normal', lineHeight: 1.2 }}>
                        Referente legale
                        <br />
                        esterno
                      </Box>
                    </TableSortLabel>
                  </TableCell>
                )}
                {columnVisibility.tribunale && <TableCell>Tribunale</TableCell>}
                {columnVisibility.annoRg && <TableCell>Anno R.G.</TableCell>}
                {columnVisibility.valorePratica && (
                  <TableCell sortDirection={orderBy === 'valore_pratica' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'valore_pratica'}
                      direction={orderBy === 'valore_pratica' ? order : 'asc'}
                      onClick={() => handleRequestSort('valore_pratica')}
                    >
                      Valore pratica
                    </TableSortLabel>
                  </TableCell>
                )}
                {columnVisibility.contributo && <TableCell>Contributo</TableCell>}
                {columnVisibility.priorita && (
                  <TableCell sortDirection={orderBy === 'priorita' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'priorita'}
                      direction={orderBy === 'priorita' ? order : 'asc'}
                      onClick={() => handleRequestSort('priorita')}
                    >
                      Priorità
                    </TableSortLabel>
                  </TableCell>
                )}
                {columnVisibility.urgente && (
                  <TableCell sortDirection={orderBy === 'urgente' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'urgente'}
                      direction={orderBy === 'urgente' ? order : 'asc'}
                      onClick={() => handleRequestSort('urgente')}
                    >
                      Urgente
                    </TableSortLabel>
                  </TableCell>
                )}
                {columnVisibility.dataIscrizioneRuolo && (
                  <TableCell sortDirection={orderBy === 'data_iscrizione_ruolo' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'data_iscrizione_ruolo'}
                      direction={orderBy === 'data_iscrizione_ruolo' ? order : 'asc'}
                      onClick={() => handleRequestSort('data_iscrizione_ruolo')}
                    >
                      Iscrizione a ruolo
                    </TableSortLabel>
                  </TableCell>
                )}
                {columnVisibility.termineCostituzione && (
                  <TableCell sortDirection={orderBy === 'termine_costituzione' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'termine_costituzione'}
                      direction={orderBy === 'termine_costituzione' ? order : 'asc'}
                      onClick={() => handleRequestSort('termine_costituzione')}
                    >
                      Termine costituzione
                    </TableSortLabel>
                  </TableCell>
                )}
                {columnVisibility.termineMemorie && (
                  <TableCell sortDirection={orderBy === 'termine_memorie' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'termine_memorie'}
                      direction={orderBy === 'termine_memorie' ? order : 'asc'}
                      onClick={() => handleRequestSort('termine_memorie')}
                    >
                      Termine memorie
                    </TableSortLabel>
                  </TableCell>
                )}
                {columnVisibility.dataApertura && (
                  <TableCell sortDirection={orderBy === 'data_apertura' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'data_apertura'}
                      direction={orderBy === 'data_apertura' ? order : 'asc'}
                      onClick={() => handleRequestSort('data_apertura')}
                    >
                      Data apertura
                    </TableSortLabel>
                  </TableCell>
                )}
                {columnVisibility.dataScadenza && (
                  <TableCell sortDirection={orderBy === 'data_scadenza' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'data_scadenza'}
                      direction={orderBy === 'data_scadenza' ? order : 'asc'}
                      onClick={() => handleRequestSort('data_scadenza')}
                    >
                      Data scadenza
                    </TableSortLabel>
                  </TableCell>
                )}
                {columnVisibility.dateUdienze && (
                  <TableCell>
                    <Box component="span" sx={{ display: 'inline-block', whiteSpace: 'normal', lineHeight: 1.2 }}>
                      Date
                      <br />
                      udienze
                    </Box>
                  </TableCell>
                )}
                {columnVisibility.updatedAt && (
                  <TableCell sortDirection={orderBy === 'updated_at' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'updated_at'}
                      direction={orderBy === 'updated_at' ? order : 'asc'}
                      onClick={() => handleRequestSort('updated_at')}
                    >
                      Aggiornata il
                    </TableSortLabel>
                  </TableCell>
                )}
                <TableCell align="right">Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={totalColumns} align="center" sx={{ py: 5 }}>
                    <CircularProgress size={26} />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={totalColumns} align="center" sx={{ py: 5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                      <Filter size={16} />
                      <Typography variant="body2" color="text.secondary">
                        Nessuna pratica U/O trovata
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      {(() => {
                        const isComplete = isRgComplete(row.numero_rg, row.anno_rg);
                        return (
                          <Chip
                            label={formatRgValue(row.numero_rg, row.anno_rg)}
                            size="small"
                            variant="outlined"
                            sx={{
                              fontWeight: 700,
                              color: isComplete ? '#166534' : '#b91c1c',
                              borderColor: isComplete ? '#86efac' : '#fca5a5',
                              backgroundColor: isComplete ? '#f0fdf4' : '#fef2f2'
                            }}
                          />
                        );
                      })()}
                    </TableCell>
                    {columnVisibility.codice && (
                      <TableCell sx={{ fontWeight: 600 }}>{row.codice_pratica}</TableCell>
                    )}
                    {columnVisibility.oggetto && <TableCell>{row.oggetto || '-'}</TableCell>}
                    <TableCell>
                      <Chip label={row.stato_pratica || 'Da definire'} size="small" sx={statusChipSx(row.stato_pratica)} />
                    </TableCell>
                    {columnVisibility.id && <TableCell>{row.id}</TableCell>}
                    {columnVisibility.cliente && (
                      <TableCell>
                        {renderContactPreview({
                          name:
                            row.cliente_nome ||
                            (row.cliente_id
                              ? customerLabelCache[String(row.cliente_id)] || `ID ${row.cliente_id}`
                              : '-'),
                          phone: row.cliente_telefono,
                          email: row.cliente_email
                        })}
                      </TableCell>
                    )}
                    {columnVisibility.controparte && (
                      <TableCell>
                        <Box sx={{ display: 'grid', gap: 0.35, minWidth: 240 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                            {row.controparte_nome ||
                              (row.controparte_id
                                ? issuerLabelCache[String(row.controparte_id)] || `ID ${row.controparte_id}`
                                : '-')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Creditore principale:{' '}
                            {row.creditore_principale_nome ||
                              (row.creditore_principale_id
                                ? issuerLabelCache[String(row.creditore_principale_id)] || `ID ${row.creditore_principale_id}`
                                : '-')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Posizioni credito:{' '}
                            {Array.isArray(row.tipi_posizione_credito_nomi) && row.tipi_posizione_credito_nomi.length
                              ? row.tipi_posizione_credito_nomi.join(', ')
                              : '-'}
                          </Typography>
                        </Box>
                      </TableCell>
                    )}
                    {columnVisibility.posizioniCredito && (
                      <TableCell>
                        {Array.isArray(row.tipi_posizione_credito_nomi) && row.tipi_posizione_credito_nomi.length
                          ? row.tipi_posizione_credito_nomi.join(', ')
                          : '-'}
                      </TableCell>
                    )}
                    {columnVisibility.creditorePrincipale && (
                      <TableCell>
                        {row.creditore_principale_nome ||
                          (row.creditore_principale_id
                            ? issuerLabelCache[String(row.creditore_principale_id)] || `ID ${row.creditore_principale_id}`
                            : '-')}
                      </TableCell>
                    )}
                    {columnVisibility.referenteLegaleInterno && (
                      <TableCell>
                        {renderContactPreview({
                          name:
                            row.referente_legale_interno_nome ||
                            (row.referente_legale_interno_id
                              ? referentiInterniLabelCache[String(row.referente_legale_interno_id)] ||
                                `ID ${row.referente_legale_interno_id}`
                              : '-'),
                          email: row.referente_legale_interno_email
                        })}
                      </TableCell>
                    )}
                    {columnVisibility.referenteContabile && (
                      <TableCell>
                        {renderContactPreview({
                          name:
                            row.referente_contabile_nome ||
                            (row.referente_contabile_id
                              ? referentiContabiliLabelCache[String(row.referente_contabile_id)] ||
                                `ID ${row.referente_contabile_id}`
                              : '-'),
                          email: row.referente_contabile_email
                        })}
                      </TableCell>
                    )}
                    {columnVisibility.referenteLegaleEsterno && (
                      <TableCell>
                        {renderContactPreview({
                          name:
                            row.referente_legale_esterno_nome ||
                            (row.referente_legale_esterno_id
                              ? referentiEsterniLabelCache[String(row.referente_legale_esterno_id)] ||
                                `ID ${row.referente_legale_esterno_id}`
                              : '-'),
                          phone: row.referente_legale_esterno_telefono,
                          email: row.referente_legale_esterno_email
                        })}
                      </TableCell>
                    )}
                    {columnVisibility.tribunale && (
                      <TableCell>{row.tribunale_nome || tribunaleLabelById.get(String(row.tribunale_id)) || '-'}</TableCell>
                    )}
                    {columnVisibility.annoRg && <TableCell>{row.anno_rg || '-'}</TableCell>}
                    {columnVisibility.valorePratica && <TableCell>{formatCurrency(row.valore_pratica)}</TableCell>}
                    {columnVisibility.contributo && <TableCell>{formatCurrency(row.contributo_unificato)}</TableCell>}
                    {columnVisibility.priorita && (
                      <TableCell>
                        {row.priorita === 2 ? 'Alta' : row.priorita === 1 ? 'Media' : 'Bassa'}
                      </TableCell>
                    )}
                    {columnVisibility.urgente && <TableCell>{row.urgente ? 'Si' : 'No'}</TableCell>}
                    {columnVisibility.dataIscrizioneRuolo && <TableCell>{formatDate(row.data_iscrizione_ruolo)}</TableCell>}
                    {columnVisibility.termineCostituzione && <TableCell>{formatDate(row.termine_costituzione)}</TableCell>}
                    {columnVisibility.termineMemorie && <TableCell>{formatDate(row.termine_memorie)}</TableCell>}
                    {columnVisibility.dataApertura && <TableCell>{formatDate(row.data_apertura)}</TableCell>}
                    {columnVisibility.dataScadenza && <TableCell>{formatDate(row.data_scadenza)}</TableCell>}
                    {columnVisibility.dateUdienze && (
                      <TableCell>
                        {(() => {
                          const id = row?.id ? String(row.id) : '';
                          if (!id) {
                            return (
                              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, color: 'text.disabled' }}>
                                <CalendarDays size={16} />
                                <Typography variant="body2" color="text.secondary">
                                  -
                                </Typography>
                              </Box>
                            );
                          }
                          if (udienzeDatesLoadingByPraticaId[id]) {
                            return (
                              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, color: 'text.disabled' }}>
                                <CalendarDays size={16} />
                                <CircularProgress size={16} color="inherit" />
                              </Box>
                            );
                          }
                          const dates = udienzeDatesByPraticaId[id] || [];
                          if (!Array.isArray(dates) || dates.length === 0) {
                            return (
                              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, color: 'text.disabled' }}>
                                <CalendarDays size={16} />
                                <Typography variant="body2" color="text.secondary">
                                  -
                                </Typography>
                              </Box>
                            );
                          }
                          const firstTwo = dates.slice(0, 2).map((d) => formatDate(d)).filter(Boolean);
                          const extraCount = dates.length - firstTwo.length;
                          return (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                              <Box sx={{ display: 'inline-flex', alignItems: 'center', color: 'success.main' }}>
                                <CalendarDays size={16} />
                              </Box>
                              <Typography variant="body2">{firstTwo.join(', ')}</Typography>
                              {extraCount > 0 && (
                                <Typography variant="caption" color="text.secondary">
                                  +{extraCount}
                                </Typography>
                              )}
                            </Box>
                          );
                        })()}
                      </TableCell>
                    )}
                    {columnVisibility.updatedAt && <TableCell>{formatDateTime(row.updated_at)}</TableCell>}
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5 }}>
                        <Tooltip
                          title={isValidEmail(row?.cliente_email) ? 'Invia email aggiornamento' : 'Email cliente non disponibile'}
                        >
                          <span>
                            <IconButton
                              color="secondary"
                              size="small"
                              onClick={() => openEmailDialog(row)}
                              disabled={!isValidEmail(row?.cliente_email)}
                            >
                              <Mail size={16} />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Modifica pratica U/O">
                          <IconButton color="primary" size="small" onClick={() => openEditDialog(row)}>
                            <EditIcon size={16} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Dettaglio pratica/Udienze">
                          <IconButton color="info" size="small" onClick={() => openDetailDialog(row)}>
                            <Eye size={16} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Elimina pratica U/O">
                          <IconButton color="error" size="small" onClick={() => openDeleteDialog(row)}>
                            <Trash2 size={16} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, nextPage) => setPage(nextPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50]}
          labelRowsPerPage="Righe per pagina"
        />
      </StyledCard>

      <Dialog open={emailDialogOpen} onClose={closeEmailDialog} maxWidth="md" fullWidth>
        <Paper
          variant="outlined"
          sx={{
            borderRadius: 2,
            overflow: 'hidden',
            m: 0,
            maxHeight: '92vh',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <DialogTitle sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  bgcolor: '#eff6ff',
                  color: '#2563eb',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}
              >
                <Mail size={20} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 800, lineHeight: 1.15 }}>Invia email</Typography>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {emailTarget?.codice_pratica ? `Pratica ${emailTarget.codice_pratica}` : 'Aggiornamento pratica Urgenza/Opposizione'}
                </Typography>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 2, pt: 2, overflowY: 'auto', minHeight: 0 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  size="small"
                  options={emailAccounts || []}
                  getOptionLabel={(option) => String(option.email || option.name || `Account ${option.id}`)}
                  value={(emailAccounts || []).find((a) => String(a.id) === emailAccountId) || null}
                  isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                  onChange={(e, newValue) => setEmailAccountId(newValue ? String(newValue.id) : '')}
                  disabled={emailAccountsLoading || emailSending}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Account mittente"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {emailAccountsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  label="A"
                  value={emailForm.to}
                  onChange={(e) => setEmailForm((p) => ({ ...p, to: String(e.target.value || '') }))}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  label="CC"
                  value={emailForm.cc}
                  onChange={(e) => setEmailForm((p) => ({ ...p, cc: String(e.target.value || '') }))}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  label="Oggetto"
                  value={emailForm.subject}
                  onChange={(e) => setEmailForm((p) => ({ ...p, subject: String(e.target.value || '') }))}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 1, fontWeight: 600 }}>
                  Messaggio
                </Typography>
                <Box
                  sx={{
                    '& .quill': {
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      overflow: 'hidden'
                    },
                    '& .ql-toolbar': {
                      border: 'none',
                      borderBottom: '1px solid',
                      borderBottomColor: 'divider',
                      bgcolor: 'background.paper'
                    },
                    '& .ql-container': {
                      border: 'none',
                      minHeight: 240,
                      fontSize: 14,
                      fontFamily: 'inherit'
                    }
                  }}
                >
                  <ReactQuill
                    theme="snow"
                    value={emailForm.body}
                    onChange={(content) => setEmailForm((p) => ({ ...p, body: content }))}
                    modules={{
                      toolbar: [
                        [{ font: [] }],
                        [{ size: [] }],
                        ['bold', 'italic', 'underline', 'strike'],
                        [{ script: 'sub' }, { script: 'super' }],
                        [{ align: [] }],
                        [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
                        ['blockquote', 'code-block'],
                        ['link', 'image'],
                        ['clean']
                      ]
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions
            sx={{
              p: 2,
              gap: 1,
              borderTop: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              position: 'sticky',
              bottom: 0,
              zIndex: 2
            }}
          >
            <Button variant="outlined" onClick={closeEmailDialog} disabled={emailSending}>
              Annulla
            </Button>
            <Button
              variant="contained"
              onClick={handleSendEmail}
              disabled={emailSending}
              sx={(theme) => ({
                bgcolor: theme.palette.mode === 'dark' ? '#f9fafb' : '#111827',
                color: theme.palette.mode === 'dark' ? '#111827' : '#ffffff',
                '&:hover': { bgcolor: theme.palette.mode === 'dark' ? '#e5e7eb' : '#0f172a' }
              })}
            >
              {emailSending ? 'Invio...' : 'Invia'}
            </Button>
          </DialogActions>
        </Paper>
      </Dialog>

      <Dialog
        open={dialogOpen}
        onClose={() => {
          if (!saving) {
            closeFormDialog();
          }
        }}
        maxWidth="xl"
        fullWidth
      >
        <Paper
          variant="outlined"
          sx={{
            borderRadius: 2,
            overflow: 'hidden',
            m: 0,
            maxHeight: '92vh',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {editingRow ? 'Modifica pratica U/O' : 'Nuova pratica U/O'}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Compila i dati per creare o aggiornare la pratica
            </Typography>
          </Box>

          <Box sx={{ p: 3, pb: 2, overflowY: 'auto', minHeight: 0 }}>
            <Tabs
              value={formTab}
              onChange={(_event, value) => setFormTab(value)}
              sx={{
                mb: 3,
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 700,
                  minHeight: 52,
                  alignItems: 'flex-start',
                  px: 1.5
                }
              }}
            >
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 22,
                        height: 22,
                        borderRadius: '50%',
                        display: 'grid',
                        placeItems: 'center',
                        fontSize: 12,
                        fontWeight: 700,
                        border: '1px solid',
                        borderColor: formTab > 0 ? '#16a34a' : '#93c5fd',
                        color: formTab > 0 ? '#16a34a' : '#2563eb',
                        bgcolor: formTab > 0 ? '#dcfce7' : '#eff6ff'
                      }}
                    >
                      {formTab > 0 ? '✓' : '1'}
                    </Box>
                    Dati Base
                  </Box>
                }
              />
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 22,
                        height: 22,
                        borderRadius: '50%',
                        display: 'grid',
                        placeItems: 'center',
                        fontSize: 12,
                        fontWeight: 700,
                        border: '1px solid',
                        borderColor: formTab > 1 ? '#16a34a' : '#93c5fd',
                        color: formTab > 1 ? '#16a34a' : '#2563eb',
                        bgcolor: formTab > 1 ? '#dcfce7' : '#eff6ff'
                      }}
                    >
                      {formTab > 1 ? '✓' : '2'}
                    </Box>
                    Procedura & Economico
                  </Box>
                }
              />
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 22,
                        height: 22,
                        borderRadius: '50%',
                        display: 'grid',
                        placeItems: 'center',
                        fontSize: 12,
                        fontWeight: 700,
                        border: '1px solid',
                        borderColor: formTab > 2 ? '#16a34a' : '#93c5fd',
                        color: formTab > 2 ? '#16a34a' : '#2563eb',
                        bgcolor: formTab > 2 ? '#dcfce7' : '#eff6ff'
                      }}
                    >
                      {formTab > 2 ? '✓' : '3'}
                    </Box>
                    Scadenze procedurali
                  </Box>
                }
              />
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 22,
                        height: 22,
                        borderRadius: '50%',
                        display: 'grid',
                        placeItems: 'center',
                        fontSize: 12,
                        fontWeight: 700,
                        border: '1px solid',
                        borderColor: formTab > 3 ? '#16a34a' : '#93c5fd',
                        color: formTab > 3 ? '#16a34a' : '#2563eb',
                        bgcolor: formTab > 3 ? '#dcfce7' : '#eff6ff'
                      }}
                    >
                      {formTab > 3 ? '✓' : '4'}
                    </Box>
                    Dettagli
                  </Box>
                }
              />
              <Tab
                disabled={!editingRow?.id}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 22,
                        height: 22,
                        borderRadius: '50%',
                        display: 'grid',
                        placeItems: 'center',
                        fontSize: 12,
                        fontWeight: 700,
                        border: '1px solid',
                        borderColor: '#93c5fd',
                        color: '#2563eb',
                        bgcolor: '#eff6ff'
                      }}
                    >
                      5
                    </Box>
                    Note
                  </Box>
                }
              />
              <Tab
                disabled={!editingRow?.id}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 22,
                        height: 22,
                        borderRadius: '50%',
                        display: 'grid',
                        placeItems: 'center',
                        fontSize: 12,
                        fontWeight: 700,
                        border: '1px solid',
                        borderColor: '#93c5fd',
                        color: '#2563eb',
                        bgcolor: '#eff6ff'
                      }}
                    >
                      6
                    </Box>
                    Allegati
                  </Box>
                }
              />
              <Tab
                disabled={!editingRow?.id}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 22,
                        height: 22,
                        borderRadius: '50%',
                        display: 'grid',
                        placeItems: 'center',
                        fontSize: 12,
                        fontWeight: 700,
                        border: '1px solid',
                        borderColor: '#93c5fd',
                        color: '#2563eb',
                        bgcolor: '#eff6ff'
                      }}
                    >
                      7
                    </Box>
                    Udienze
                  </Box>
                }
              />
            </Tabs>

            {formTab === 0 && (
              <Grid container spacing={2.5} sx={{ mt: 0.1 }}>
                <Grid item xs={12} lg={8}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                      Informazioni generali
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="Codice pratica U/O"
                          value={form.codice_pratica}
                          onChange={(e) => setForm((prev) => ({ ...prev, codice_pratica: e.target.value }))}
                          placeholder="Lascia vuoto per generazione automatica"
                          helperText={!editingRow?.id ? 'Se vuoto, viene generato automaticamente al salvataggio.' : ''}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <FormControl fullWidth>
                          <InputLabel>Stato pratica U/O *</InputLabel>
                          <Select
                            label="Stato pratica U/O *"
                            value={form.stato_pratica}
                            onChange={(e) => setForm((prev) => ({ ...prev, stato_pratica: e.target.value }))}
                            disabled={Boolean(form.stato_auto)}
                          >
                            {statusOptions.map((status) => (
                              <MenuItem key={status} value={status}>
                                {status}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <FormControl fullWidth>
                          <InputLabel>Tipo atto ricevuto *</InputLabel>
                          <Select
                            label="Tipo atto ricevuto *"
                            value={form.tipo_atto_id}
                            onChange={(e) => setForm((prev) => ({ ...prev, tipo_atto_id: e.target.value }))}
                          >
                            <MenuItem value="">
                              <em>Nessuno</em>
                            </MenuItem>
                            {tipiAttoOptions.map((item) => (
                              <MenuItem key={item.id} value={item.id}>
                                {item.nome}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <FormControl fullWidth>
                          <InputLabel>Tipo azione legale *</InputLabel>
                          <Select
                            label="Tipo azione legale *"
                            value={form.tipo_pratica_azione_id}
                            onChange={(e) =>
                              setForm((prev) => ({ ...prev, tipo_pratica_azione_id: e.target.value }))
                            }
                          >
                            <MenuItem value="">
                              <em>Nessuno</em>
                            </MenuItem>
                            {tipiPraticaAzioneOptions.map((item) => (
                              <MenuItem key={item.id} value={item.id}>
                                {item.nome}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={8}>
                        <Autocomplete
                          multiple
                          options={tipiPosizioneCreditoOptions}
                          value={selectedTipiPosizioneCreditoOptions}
                          onChange={(_event, values) =>
                            setForm((prev) => ({
                              ...prev,
                              tipi_posizione_credito_ids: values.map((item) => Number(item.id)).filter((id) => Number.isInteger(id) && id > 0)
                            }))
                          }
                          isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                          getOptionLabel={(option) => option?.nome || option?.codice || `ID ${option?.id}`}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Posizioni credito riferite"
                              placeholder="Seleziona una o piu posizioni (es. mutuo, fido)"
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Autocomplete
                          options={tribunaliOptions}
                          value={selectedTribunaleOption}
                          onChange={(_, value) => {
                            if (value?.isCreateOption) {
                              openCreateTribunaleDialog(value.inputValue || value.nome || '');
                              return;
                            }
                            setForm((prev) => ({ ...prev, tribunale_id: value?.id ? Number(value.id) : '' }));
                          }}
                          filterOptions={(options, params) => {
                            const filtered = tribunaleFilterOptions(options, params);
                            const inputValue = String(params.inputValue || '').trim();
                            if (!inputValue) return filtered;
                            const exists = options.some((item) =>
                              String([item?.nome, item?.citta].filter(Boolean).join(' - '))
                                .toLowerCase()
                                .includes(inputValue.toLowerCase())
                            );
                            if (!exists) {
                              filtered.push({
                                id: `new-${inputValue}`,
                                nome: inputValue,
                                citta: '',
                                isCreateOption: true,
                                inputValue
                              });
                            }
                            return filtered;
                          }}
                          isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                          getOptionLabel={(option) => {
                            if (!option) return '';
                            if (option.isCreateOption) return `Aggiungi "${option.inputValue || option.nome}"`;
                            const fromMap = tribunaleLabelById.get(String(option.id));
                            if (fromMap) return fromMap;
                            const label = [option.nome, option.citta].filter(Boolean).join(' - ');
                            return label || `ID ${option.id}`;
                          }}
                          renderOption={(props, option) => (
                            <Box component="li" {...props}>
                              {option?.isCreateOption
                                ? `+ Aggiungi "${option.inputValue || option.nome}"`
                                : [option.nome, option.citta].filter(Boolean).join(' - ') || `ID ${option.id}`}
                            </Box>
                          )}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Tribunale *"
                              placeholder="Cerca tribunale..."
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Autocomplete
                          options={customerOptions}
                          loading={customerLoading}
                          value={selectedCustomerOption}
                          inputValue={customerInputValue}
                          onInputChange={(_, value) => setCustomerInputValue(value)}
                          onChange={(_, value) =>
                            setForm((prev) => ({ ...prev, cliente_id: value?.id ? Number(value.id) : '' }))
                          }
                          isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                          getOptionLabel={(option) => formatCustomerLabel(option)}
                          renderOption={(props, option) => (
                            <Box component="li" {...props}>
                              {renderContactPreview({
                                name: formatCustomerLabel(option),
                                phone: option.telephone,
                                email: option.email,
                                fallback: `ID ${option.id}`
                              })}
                            </Box>
                          )}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Cliente *"
                              placeholder="Cerca cliente..."
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {customerLoading ? <CircularProgress color="inherit" size={18} /> : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                )
                              }}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Autocomplete
                          options={issuerOptions}
                          loading={issuerLoading}
                          value={selectedIssuerOption}
                          inputValue={issuerInputValue}
                          onInputChange={(_, value) => setIssuerInputValue(value)}
                          onChange={(_, value) =>
                            setForm((prev) => ({ ...prev, controparte_id: value?.id ? Number(value.id) : '' }))
                          }
                          isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                          getOptionLabel={(option) => formatIssuerLabel(option)}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Controparte *"
                              placeholder="Cerca controparte..."
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {issuerLoading ? <CircularProgress color="inherit" size={18} /> : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                )
                              }}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Autocomplete
                          options={issuerOptions}
                          loading={issuerLoading}
                          value={selectedCreditoreOption}
                          inputValue={creditoreInputValue}
                          onInputChange={(_, value) => setCreditoreInputValue(value)}
                          onChange={(_, value) =>
                            setForm((prev) => ({
                              ...prev,
                              creditore_principale_id: value?.id ? Number(value.id) : ''
                            }))
                          }
                          isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                          getOptionLabel={(option) => formatIssuerLabel(option)}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Creditore principale"
                              placeholder="Cerca creditore..."
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {issuerLoading ? <CircularProgress color="inherit" size={18} /> : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                )
                              }}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Autocomplete
                          options={referentiLegaliInterniOptions}
                          value={selectedReferenteInternoOption}
                          onChange={(_, value) =>
                            setForm((prev) => ({
                              ...prev,
                              referente_legale_interno_id: value?.id ? Number(value.id) : ''
                            }))
                          }
                          isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                          getOptionLabel={(option) => formatReferenteInternoLabel(option)}
                          renderOption={(props, option) => (
                            <Box component="li" {...props}>
                              {renderContactPreview({
                                name: option.name || formatReferenteInternoLabel(option),
                                email: option.email,
                                fallback: `ID ${option.id}`
                              })}
                            </Box>
                          )}
                          renderInput={(params) => <TextField {...params} label="Referente legale interno" />}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Autocomplete
                          options={referentiLegaliEsterniOptions}
                          value={selectedReferenteEsternoOption}
                          onChange={(_, value) =>
                            setForm((prev) => ({
                              ...prev,
                              referente_legale_esterno_id: value?.id ? Number(value.id) : ''
                            }))
                          }
                          isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                          getOptionLabel={(option) => formatReferenteEsternoLabel(option)}
                          renderOption={(props, option) => (
                            <Box component="li" {...props}>
                              {renderContactPreview({
                                name: [option.nome, option.cognome].filter(Boolean).join(' '),
                                phone: option.telefono,
                                email: option.email,
                                fallback: `ID ${option.id}`
                              })}
                            </Box>
                          )}
                          renderInput={(params) => <TextField {...params} label="Referente legale esterno" />}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <FormControl fullWidth>
                          <InputLabel>Priorità</InputLabel>
                          <Select
                            label="Priorità"
                            value={form.priorita}
                            onChange={(e) => setForm((prev) => ({ ...prev, priorita: Number(e.target.value) }))}
                          >
                            <MenuItem value={0}>Bassa</MenuItem>
                            <MenuItem value={1}>Media</MenuItem>
                            <MenuItem value={2}>Alta</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={8}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={Boolean(form.urgente)}
                                onChange={(e) => setForm((prev) => ({ ...prev, urgente: e.target.checked }))}
                              />
                            }
                            label="Urgente"
                            sx={{ mt: { xs: 0, md: 1 } }}
                          />
                          <FormControlLabel
                            control={
                              <Switch
                                checked={Boolean(form.stato_auto)}
                                onChange={(e) =>
                                  setForm((prev) => ({
                                    ...prev,
                                    stato_auto: e.target.checked
                                  }))
                                }
                              />
                            }
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Box component="span">Auto stato</Box>
                                <Tooltip title="Dettaglio regole Auto stato">
                                  <IconButton
                                    size="small"
                                    onClick={(event) => {
                                      event.preventDefault();
                                      event.stopPropagation();
                                      setAutoStatusInfoOpen(true);
                                    }}
                                    aria-label="Apri dettaglio Auto stato"
                                    sx={{ p: 0.25 }}
                                  >
                                    <HelpCircle size={16} />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            }
                            sx={{ mt: { xs: 0, md: 1 } }}
                          />
                        </Box>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}>
                          Se attivo: regole automatiche su dati pratica, udienze e allegati.
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
                <Grid item xs={12} lg={4}>
                  <Paper variant="outlined" sx={{ p: 2, position: { lg: 'sticky' }, top: { lg: 16 } }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                      Riepilogo pratica
                    </Typography>
                    <Box sx={{ display: 'grid', gap: 1.25 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <FileText size={16} color="#64748b" />
                        <Typography variant="body2"><Box component="span" sx={{ color: 'text.secondary', mr: 1 }}>Codice pratica</Box>{form.codice_pratica?.trim() || '-'}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <FileText size={16} color="#64748b" />
                        <Typography variant="body2"><Box component="span" sx={{ color: 'text.secondary', mr: 1 }}>Tipo atto</Box>{tipiAttoOptions.find((item) => String(item.id) === String(form.tipo_atto_id))?.nome || '-'}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <Gavel size={16} color="#64748b" />
                        <Typography variant="body2"><Box component="span" sx={{ color: 'text.secondary', mr: 1 }}>Azione legale</Box>{tipiPraticaAzioneOptions.find((item) => String(item.id) === String(form.tipo_pratica_azione_id))?.nome || '-'}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <FileText size={16} color="#64748b" />
                        <Typography variant="body2">
                          <Box component="span" sx={{ color: 'text.secondary', mr: 1 }}>Posizioni credito</Box>
                          {selectedTipiPosizioneCreditoOptions.length > 0
                            ? selectedTipiPosizioneCreditoOptions.map((item) => item.nome).join(', ')
                            : '-'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <User size={16} color="#64748b" />
                        <Typography variant="body2"><Box component="span" sx={{ color: 'text.secondary', mr: 1 }}>Cliente</Box>{selectedCustomerOption ? formatCustomerLabel(selectedCustomerOption) : '-'}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <Building2 size={16} color="#64748b" />
                        <Typography variant="body2"><Box component="span" sx={{ color: 'text.secondary', mr: 1 }}>Controparte</Box>{selectedIssuerOption ? formatIssuerLabel(selectedIssuerOption) : '-'}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <Landmark size={16} color="#64748b" />
                        <Typography variant="body2"><Box component="span" sx={{ color: 'text.secondary', mr: 1 }}>Tribunale</Box>{tribunaliOptions.find((item) => String(item.id) === String(form.tribunale_id))?.nome || '-'}</Typography>
                      </Box>
                      <Box sx={{ pt: 0.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Chip size="small" label={form.stato_pratica || 'Da definire'} sx={statusChipSx(form.stato_pratica)} />
                        <Chip size="small" label={getAutoStatusLabel(form.stato_auto)} sx={getAutoStatusChipSx(form.stato_auto)} />
                        <Chip size="small" label={`Priorità ${priorityMeta.label}`} sx={{ backgroundColor: `${priorityMeta.color}14`, color: priorityMeta.color, border: `1px solid ${priorityMeta.color}40`, fontWeight: 600 }} />
                        <Chip size="small" label={form.urgente ? 'Urgente: Sì' : 'Urgente: No'} color={form.urgente ? 'success' : 'default'} variant={form.urgente ? 'filled' : 'outlined'} />
                      </Box>
                    </Box>
                    <Box sx={{ mt: 2, px: 1.5, py: 1, borderRadius: 1.5, bgcolor: '#eff6ff', border: '1px solid #bfdbfe' }}>
                      <Typography variant="caption" sx={{ color: '#1d4ed8', fontWeight: 600 }}>
                        I campi contrassegnati con * sono obbligatori.
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            )}

            {formTab === 1 && (
              <Grid container spacing={2.5} sx={{ mt: 0.1 }}>
                <Grid item xs={12} lg={8}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                          Dati procedurali
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <TextField fullWidth label="Numero R.G." value={form.numero_rg} onChange={(e) => setForm((prev) => ({ ...prev, numero_rg: e.target.value }))} />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField fullWidth type="number" label="Anno R.G." value={form.anno_rg} onChange={(e) => setForm((prev) => ({ ...prev, anno_rg: e.target.value }))} />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField fullWidth type="number" inputProps={{ step: '0.01' }} label="Valore pratica" value={form.valore_pratica} onChange={(e) => setForm((prev) => ({ ...prev, valore_pratica: e.target.value }))} />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField fullWidth type="number" inputProps={{ step: '0.01' }} label="Contributo unificato" value={form.contributo_unificato} onChange={(e) => setForm((prev) => ({ ...prev, contributo_unificato: e.target.value }))} />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Autocomplete
                              options={referentiContabiliOptions}
                              value={selectedReferenteContabileOption}
                              onChange={(_, value) =>
                                setForm((prev) => ({
                                  ...prev,
                                  referente_contabile_id: value?.id ? Number(value.id) : ''
                                }))
                              }
                              isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                              getOptionLabel={(option) => formatReferenteInternoLabel(option)}
                              renderOption={(props, option) => (
                                <Box component="li" {...props}>
                                  {renderContactPreview({
                                    name: option.name || formatReferenteInternoLabel(option),
                                    email: option.email,
                                    fallback: `ID ${option.id}`
                                  })}
                                </Box>
                              )}
                              renderInput={(params) => <TextField {...params} label="Referente contabile" />}
                            />
                          </Grid>
                        </Grid>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          Date pratica
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                          Timeline interna della pratica U/O.
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <TextField fullWidth type="date" label="Data firma" value={form.data_firma} onChange={(e) => setForm((prev) => ({ ...prev, data_firma: e.target.value }))} InputLabelProps={{ shrink: true }} />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField fullWidth type="date" label="Data apertura" value={form.data_apertura} onChange={(e) => setForm((prev) => ({ ...prev, data_apertura: e.target.value }))} InputLabelProps={{ shrink: true }} />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField fullWidth type="date" label="Data scadenza" value={form.data_scadenza} onChange={(e) => setForm((prev) => ({ ...prev, data_scadenza: e.target.value }))} InputLabelProps={{ shrink: true }} />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField fullWidth type="date" label="Data chiusura" value={form.data_chiusura} onChange={(e) => setForm((prev) => ({ ...prev, data_chiusura: e.target.value }))} InputLabelProps={{ shrink: true }} />
                          </Grid>
                        </Grid>
                      </Paper>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12} lg={4}>
                  <Paper variant="outlined" sx={{ p: 2, position: { lg: 'sticky' }, top: { lg: 16 } }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Riepilogo pratica</Typography>
                    <Box sx={{ display: 'grid', gap: 1.25 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FileText size={16} color="#64748b" />
                        <Typography variant="body2"><Box component="span" sx={{ color: 'text.secondary', mr: 1 }}>Codice pratica</Box>{form.codice_pratica?.trim() || '-'}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Activity size={16} color="#64748b" />
                        <Typography variant="body2"><Box component="span" sx={{ color: 'text.secondary', mr: 1 }}>Stato</Box>{form.stato_pratica || '-'}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Activity size={16} color="#64748b" />
                        <Chip size="small" label={getAutoStatusLabel(form.stato_auto)} sx={getAutoStatusChipSx(form.stato_auto)} />
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <User size={16} color="#64748b" />
                        <Typography variant="body2">
                          <Box component="span" sx={{ color: 'text.secondary', mr: 1 }}>
                            Referente contabile
                          </Box>
                          {selectedReferenteContabileOption
                            ? formatReferenteInternoLabel(selectedReferenteContabileOption)
                            : '-'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Flag size={16} color="#64748b" />
                        <Typography variant="body2"><Box component="span" sx={{ color: 'text.secondary', mr: 1 }}>Valore pratica</Box>{formatCurrency(form.valore_pratica)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Flag size={16} color="#64748b" />
                        <Typography variant="body2"><Box component="span" sx={{ color: 'text.secondary', mr: 1 }}>Contributo unificato</Box>{formatCurrency(form.contributo_unificato)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FileText size={16} color="#64748b" />
                        <Typography variant="body2"><Box component="span" sx={{ color: 'text.secondary', mr: 1 }}>Iscrizione a ruolo</Box>{formatDate(form.data_iscrizione_ruolo)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FileText size={16} color="#64748b" />
                        <Typography variant="body2"><Box component="span" sx={{ color: 'text.secondary', mr: 1 }}>Termine costituzione</Box>{formatDate(form.termine_costituzione)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FileText size={16} color="#64748b" />
                        <Typography variant="body2"><Box component="span" sx={{ color: 'text.secondary', mr: 1 }}>Termine memorie</Box>{formatDate(form.termine_memorie)}</Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            )}

            {formTab === 2 && (
              <Grid container spacing={2.5} sx={{ mt: 0.1 }}>
                <Grid item xs={12} lg={8}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                      Scadenze procedurali
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Date chiave del procedimento: iscrizione a ruolo e termini processuali di costituzione e memorie.
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          type="date"
                          label="Data iscrizione a ruolo"
                          value={form.data_iscrizione_ruolo}
                          onChange={(e) => setForm((prev) => ({ ...prev, data_iscrizione_ruolo: e.target.value }))}
                          InputLabelProps={{ shrink: true }}
                          helperText="Data di iscrizione della causa al ruolo."
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          type="date"
                          label="Termine per costituzione"
                          value={form.termine_costituzione}
                          onChange={(e) => setForm((prev) => ({ ...prev, termine_costituzione: e.target.value }))}
                          InputLabelProps={{ shrink: true }}
                          helperText="Scadenza entro cui costituirsi in giudizio."
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          type="date"
                          label="Termine per memorie"
                          value={form.termine_memorie}
                          onChange={(e) => setForm((prev) => ({ ...prev, termine_memorie: e.target.value }))}
                          InputLabelProps={{ shrink: true }}
                          helperText="Scadenza per il deposito memorie."
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
                <Grid item xs={12} lg={4}>
                  <Paper variant="outlined" sx={{ p: 2, position: { lg: 'sticky' }, top: { lg: 16 } }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                      Riepilogo scadenze
                    </Typography>
                    <Box sx={{ display: 'grid', gap: 1.25 }}>
                      <Typography variant="body2"><Box component="span" sx={{ color: 'text.secondary', mr: 1 }}>Iscrizione a ruolo</Box>{formatDate(form.data_iscrizione_ruolo)}</Typography>
                      <Typography variant="body2"><Box component="span" sx={{ color: 'text.secondary', mr: 1 }}>Termine costituzione</Box>{formatDate(form.termine_costituzione)}</Typography>
                      <Typography variant="body2"><Box component="span" sx={{ color: 'text.secondary', mr: 1 }}>Termine memorie</Box>{formatDate(form.termine_memorie)}</Typography>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            )}

            {formTab === 3 && (
              <Grid container spacing={2.5} sx={{ mt: 0.1 }}>
                <Grid item xs={12} lg={8}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                      Dettagli aggiuntivi
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField fullWidth label="Oggetto" value={form.oggetto} onChange={(e) => setForm((prev) => ({ ...prev, oggetto: e.target.value }))} />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField fullWidth label="Note" multiline minRows={6} value={form.note} onChange={(e) => setForm((prev) => ({ ...prev, note: e.target.value }))} />
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
                <Grid item xs={12} lg={4}>
                  <Paper variant="outlined" sx={{ p: 2, position: { lg: 'sticky' }, top: { lg: 16 } }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Riepilogo pratica</Typography>
                    <Box sx={{ display: 'grid', gap: 1.25 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FileText size={16} color="#64748b" />
                        <Typography variant="body2"><Box component="span" sx={{ color: 'text.secondary', mr: 1 }}>Codice pratica</Box>{form.codice_pratica?.trim() || '-'}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FileText size={16} color="#64748b" />
                        <Typography variant="body2"><Box component="span" sx={{ color: 'text.secondary', mr: 1 }}>Oggetto</Box>{form.oggetto?.trim() || '-'}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Activity size={16} color="#64748b" />
                        <Typography variant="body2"><Box component="span" sx={{ color: 'text.secondary', mr: 1 }}>Urgente</Box>{form.urgente ? 'Sì' : 'No'}</Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            )}

            {formTab === 4 && (
              <Grid container spacing={2.5} sx={{ mt: 0.1 }}>
                <Grid item xs={12} lg={5}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.25 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Button
                          size="small"
                          variant="contained"
                          sx={{
                            textTransform: 'none',
                            borderRadius: 2,
                            px: 1.5,
                            py: 0.5,
                            boxShadow: 'none'
                          }}
                        >
                          Scrivi nota
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          sx={{ textTransform: 'none', borderRadius: 2, px: 1.5, py: 0.5 }}
                          disabled
                        >
                          Modello nota
                        </Button>
                      </Box>
                      <Chip size="small" label="Nuova nota" icon={<Plus size={13} />} />
                    </Box>

                    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={5}
                        placeholder="Scrivi qui la tua nota..."
                        value={noteForm.testo}
                        onChange={(e) => setNoteForm((prev) => ({ ...prev, testo: e.target.value }))}
                        InputProps={{ sx: { borderRadius: 1.5, alignItems: 'flex-start' } }}
                      />

                      <Box
                        sx={{
                          mt: 1.25,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 1,
                          flexWrap: 'wrap'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <FormControl size="small" sx={{ minWidth: 130 }}>
                            <Select
                              value={noteForm.tipo}
                              onChange={(e) => setNoteForm((prev) => ({ ...prev, tipo: e.target.value }))}
                            >
                              {NOTE_TYPE_OPTIONS.map((item) => (
                                <MenuItem key={item.value} value={item.value}>
                                  {item.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>

                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => noteFilesInputRef.current?.click()}
                            disabled={!editingRow?.id || noteSaving}
                            startIcon={<Paperclip size={14} />}
                            sx={{ minWidth: 110, textTransform: 'none', borderRadius: 2, height: 36 }}
                          >
                            Allega file
                          </Button>
                          <input
                            ref={noteFilesInputRef}
                            type="file"
                            hidden
                            multiple
                            onChange={handleNoteFileInputChange}
                          />

                          <FormControl size="small" sx={{ minWidth: 180 }}>
                            <Select
                              value={noteForm.visibilita}
                              onChange={(e) => setNoteForm((prev) => ({ ...prev, visibilita: e.target.value }))}
                            >
                              {NOTE_VISIBILITY_OPTIONS.map((item) => (
                                <MenuItem key={item.value} value={item.value}>
                                  {item.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>

                          <FormControlLabel
                            control={
                              <Switch
                                size="small"
                                checked={Boolean(noteForm.pinned)}
                                onChange={(e) =>
                                  setNoteForm((prev) => ({ ...prev, pinned: e.target.checked }))
                                }
                              />
                            }
                            label="Fissa nota"
                            sx={{ ml: 0.5 }}
                          />
                        </Box>
                        <Button
                          variant="contained"
                          onClick={handleSaveNote}
                          disabled={!editingRow?.id || noteSaving}
                          sx={{ minWidth: 120, textTransform: 'none', borderRadius: 2, boxShadow: 'none' }}
                        >
                          Salva nota
                        </Button>
                      </Box>

                      {Array.isArray(noteForm.files) && noteForm.files.length > 0 && (
                        <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.75, pl: 0.25 }}>
                          {noteForm.files.map((file, index) => (
                            <Chip
                              key={`${file.name}_${index}`}
                              label={`${file.name} (${formatBytes(file.size)})`}
                              onDelete={() => handleRemovePendingNoteFile(index)}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      )}
                    </Paper>
                  </Paper>
                </Grid>

                <Grid item xs={12} lg={7}>
                  <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.25 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Tutte le note ({noteRows.length})
                      </Typography>
                      <FormControl size="small" sx={{ minWidth: 175 }}>
                        <Select
                          value={noteOrder}
                          onChange={(e) => setNoteOrder(e.target.value)}
                          startAdornment={
                            <InputAdornment position="start">
                              <ArrowUpDown size={14} color="#64748b" />
                            </InputAdornment>
                          }
                        >
                          <MenuItem value="desc">Piu recenti</MenuItem>
                          <MenuItem value="asc">Meno recenti</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                    {noteLoading ? (
                      <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
                        <CircularProgress size={22} />
                      </Box>
                    ) : noteRows.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        Nessuna nota presente per questa pratica.
                      </Typography>
                    ) : (
                      <Box sx={{ display: 'grid', gap: 1 }}>
                        {noteRows.map((note) => (
                          <Paper
                            key={note.id}
                            variant="outlined"
                            sx={{
                              p: 1.5,
                              borderRadius: 2,
                              borderColor: '#e8eef8',
                              position: 'relative'
                            }}
                          >
                            <Box
                              sx={{
                                position: 'absolute',
                                left: -7,
                                top: 26,
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: noteTypeColor(note.tipo)
                              }}
                            />
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                              <Avatar sx={{ width: 34, height: 34, bgcolor: noteTypeColor(note.tipo), fontSize: 13 }}>
                                {getInitials(note.autore_nome || `U${note.autore_user_id || ''}`)}
                              </Avatar>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                      {note.autore_nome || `Utente ${note.autore_user_id || '-'}`}
                                    </Typography>
                                    <Chip
                                      size="small"
                                      label={noteVisibilityShortLabel(note.visibilita)}
                                      sx={{
                                        height: 22,
                                        fontSize: 11,
                                        bgcolor: '#f3f0ff',
                                        color: '#6d28d9'
                                      }}
                                    />
                                    <Chip
                                      size="small"
                                      label={noteTypeLabel(note.tipo)}
                                      sx={{
                                        height: 22,
                                        fontSize: 11,
                                        bgcolor: '#eef2ff',
                                        color: '#1e3a8a'
                                      }}
                                    />
                                    {Boolean(note.pinned) && (
                                      <Chip size="small" label="Fissata" sx={{ height: 22, fontSize: 11 }} />
                                    )}
                                  </Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                                      {formatDateTime(note.created_at)}
                                    </Typography>
                                    <Tooltip title={note.pinned ? 'Rimuovi pin' : 'Fissa nota'}>
                                      <IconButton size="small" onClick={() => handleTogglePinNote(note)}>
                                        <Flag size={14} />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Elimina nota">
                                      <span>
                                        <IconButton
                                          size="small"
                                          color="error"
                                          onClick={() => handleRequestDeleteNote(note)}
                                          disabled={noteDeletingId === note.id}
                                        >
                                          <Trash2 size={14} />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                    <IconButton size="small" disabled>
                                      <MoreVertical size={14} />
                                    </IconButton>
                                  </Box>
                                </Box>
                                <Typography
                                  variant="body2"
                                  sx={{ mt: 0.8, color: '#334155', fontSize: 16, lineHeight: 1.55, whiteSpace: 'pre-wrap' }}
                                >
                                  {note.testo || '-'}
                                </Typography>

                                {Array.isArray(note.allegati) && note.allegati.length > 0 && (
                                  <Box sx={{ mt: 1.2, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                    {note.allegati.map((allegato) => (
                                      <Chip
                                        key={allegato.link_id}
                                        icon={<Paperclip size={14} />}
                                        label={`${allegato.original_name || `File ${allegato.media_id}`}  ${formatBytes(allegato.size_bytes)}`}
                                        onClick={() =>
                                          handleOpenNoteAttachment(allegato.media_id, allegato.original_name)
                                        }
                                        onDelete={() => handleDeleteNoteAttachment(allegato.link_id)}
                                        size="small"
                                        variant="outlined"
                                        sx={{ borderRadius: 1.5, height: 28 }}
                                      />
                                    ))}
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          </Paper>
                        ))}
                      </Box>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            )}

            {formTab === 5 && (
              <Grid container spacing={2.5} sx={{ mt: 0.1 }}>
                <Grid item xs={12} lg={8}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                      Allegati
                    </Typography>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        borderStyle: 'dashed',
                        borderColor: initialDocumentsDragActive ? '#2563eb' : 'rgba(37, 99, 235, 0.35)',
                        bgcolor: initialDocumentsDragActive ? 'rgba(239, 246, 255, 0.7)' : 'transparent'
                      }}
                      onDragEnter={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        if (editingRow?.id) setInitialDocumentsDragActive(true);
                      }}
                      onDragOver={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                      }}
                      onDragLeave={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        setInitialDocumentsDragActive(false);
                      }}
                      onDrop={handleInitialDocumentsDrop}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            Documenti iniziali ricevuti
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Carica PDF/JPG/PNG (max 10MB) relativi alla ricezione iniziale della pratica.
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <FormControl size="small" sx={{ minWidth: 220 }}>
                            <InputLabel>Categoria allegato</InputLabel>
                            <Select
                              label="Categoria allegato"
                              value={selectedInitialDocTipoCodice}
                              onChange={(event) => setSelectedInitialDocTipoCodice(event.target.value)}
                              disabled={!editingRow?.id || initialDocumentsUploading || tipiAllegatoOptions.length === 0}
                            >
                              {tipiAllegatoOptions.map((item) => (
                                <MenuItem key={item.id || item.codice} value={item.codice}>
                                  {item.nome}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <Button
                            variant="outlined"
                            onClick={() => initialDocumentsInputRef.current?.click()}
                            disabled={!editingRow?.id || initialDocumentsUploading || !selectedInitialDocTipoCodice}
                          >
                            Seleziona file
                          </Button>
                        </Box>
                        <input
                          ref={initialDocumentsInputRef}
                          type="file"
                          hidden
                          multiple
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={handleInitialDocumentsInputChange}
                        />
                      </Box>

                      {!editingRow?.id && (
                        <Box sx={{ mt: 1.5, px: 1.5, py: 1, borderRadius: 1.5, bgcolor: '#fff7ed', border: '1px solid #fed7aa' }}>
                          <Typography variant="caption" sx={{ color: '#9a3412', fontWeight: 600 }}>
                            Salva prima la pratica per poter caricare i documenti iniziali.
                          </Typography>
                        </Box>
                      )}

                      {initialDocumentsUploading && (
                        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={18} />
                          <Typography variant="body2" color="text.secondary">
                            Caricamento documenti in corso...
                          </Typography>
                        </Box>
                      )}

                      <Box sx={{ mt: 2 }}>
                        {initialDocumentsLoading ? (
                          <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
                            <CircularProgress size={20} />
                          </Box>
                        ) : initialDocuments.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            Nessun documento iniziale caricato.
                          </Typography>
                        ) : (
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Nome file</TableCell>
                                <TableCell>Categoria</TableCell>
                                <TableCell>Formato</TableCell>
                                <TableCell>Dimensione</TableCell>
                                <TableCell>Caricato il</TableCell>
                                <TableCell align="right">Azioni</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {initialDocuments.map((doc) => (
                                <TableRow key={doc.link_id} hover>
                                  <TableCell sx={{ fontWeight: 600 }}>{doc.original_name || `File ${doc.media_id}`}</TableCell>
                                  <TableCell>{resolveInitialDocumentCategoryLabel(doc)}</TableCell>
                                  <TableCell>{doc.mime_type || '-'}</TableCell>
                                  <TableCell>{formatBytes(doc.size_bytes)}</TableCell>
                                  <TableCell>{formatDateTime(doc.created_at)}</TableCell>
                                  <TableCell align="right">
                                    <Tooltip title="Apri">
                                      <span>
                                        <IconButton size="small" onClick={() => handleOpenInitialDocument(doc.media_id, doc.original_name)}>
                                          <Eye size={16} />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                    <Tooltip title="Elimina">
                                      <span>
                                        <IconButton
                                          size="small"
                                          color="error"
                                          onClick={() => handleDeleteInitialDocument(doc.link_id)}
                                          disabled={initialDocumentsDeletingLinkId === doc.link_id}
                                        >
                                          <Trash2 size={16} />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        )}
                      </Box>
                    </Paper>
                  </Paper>
                </Grid>
                <Grid item xs={12} lg={4}>
                  <Paper variant="outlined" sx={{ p: 2, position: { lg: 'sticky' }, top: { lg: 16 } }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                      Riepilogo allegati
                    </Typography>
                    <Box sx={{ display: 'grid', gap: 1.25 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FileText size={16} color="#64748b" />
                        <Typography variant="body2">
                          <Box component="span" sx={{ color: 'text.secondary', mr: 1 }}>Codice pratica</Box>
                          {form.codice_pratica?.trim() || '-'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FileText size={16} color="#64748b" />
                        <Typography variant="body2">
                          <Box component="span" sx={{ color: 'text.secondary', mr: 1 }}>Documenti caricati</Box>
                          {initialDocuments.length}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Activity size={16} color="#64748b" />
                        <Typography variant="body2">
                          <Box component="span" sx={{ color: 'text.secondary', mr: 1 }}>Stato upload</Box>
                          {!editingRow?.id ? 'Disponibile dopo salvataggio' : initialDocumentsUploading ? 'Caricamento in corso' : 'Pronto'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FileText size={16} color="#64748b" />
                        <Typography variant="body2">
                          <Box component="span" sx={{ color: 'text.secondary', mr: 1 }}>Categoria selezionata</Box>
                          {tipiAllegatoOptions.find((item) => item.codice === selectedInitialDocTipoCodice)?.nome || '-'}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            )}

            {formTab === 6 && (
              <Grid container spacing={2.5} sx={{ mt: 0.1 }}>
                <Grid item xs={12} lg={8}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                          Udienze della pratica
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Gestisci date udienza, esito e note per questa pratica
                        </Typography>
                      </Box>
                      <Button variant="contained" startIcon={<Plus size={16} />} onClick={openCreateUdienzaDialog}>
                        Nuova udienza
                      </Button>
                    </Box>

                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>#</TableCell>
                            <TableCell>Data</TableCell>
                            <TableCell>Ora</TableCell>
                            <TableCell>Esito</TableCell>
                            <TableCell>Esito libero</TableCell>
                            <TableCell>Note</TableCell>
                            <TableCell align="right">Azioni</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {udienzeLoading ? (
                            <TableRow>
                              <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                                <CircularProgress size={22} />
                              </TableCell>
                            </TableRow>
                          ) : udienzeRows.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                                <Typography variant="body2" color="text.secondary">
                                  Nessuna udienza registrata
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : (
                            udienzeRows.map((row) => (
                              <TableRow key={row.id} hover>
                                <TableCell sx={{ fontWeight: 700 }}>{row.numero_udienza}</TableCell>
                                <TableCell>{formatDate(row.data_udienza)}</TableCell>
                                <TableCell>{row.ora_udienza ? String(row.ora_udienza).slice(0, 5) : '-'}</TableCell>
                                <TableCell>
                                  {row.esito_udienza_nome ? (
                                    <Chip
                                      size="small"
                                      label={row.esito_udienza_nome}
                                      sx={{
                                        border: '1px solid #cbd5e1',
                                        bgcolor: row.esito_udienza_colore || '#f8fafc',
                                        color: '#0f172a',
                                        fontWeight: 700
                                      }}
                                    />
                                  ) : (
                                    '-'
                                  )}
                                </TableCell>
                                <TableCell>{row.esito_libero || '-'}</TableCell>
                                <TableCell>{row.note || '-'}</TableCell>
                                <TableCell align="right">
                                  <IconButton color="primary" size="small" onClick={() => openEditUdienzaDialog(row)}>
                                    <EditIcon size={16} />
                                  </IconButton>
                                  <IconButton color="error" size="small" onClick={() => openDeleteUdienzaDialog(row)}>
                                    <Trash2 size={16} />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                </Grid>
                <Grid item xs={12} lg={4}>
                  <Paper variant="outlined" sx={{ p: 2, position: { lg: 'sticky' }, top: { lg: 16 } }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                      Riepilogo udienze
                    </Typography>
                    <Box sx={{ display: 'grid', gap: 1.25 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FileText size={16} color="#64748b" />
                        <Typography variant="body2">
                          <Box component="span" sx={{ color: 'text.secondary', mr: 1 }}>Codice pratica</Box>
                          {form.codice_pratica?.trim() || '-'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Activity size={16} color="#64748b" />
                        <Typography variant="body2">
                          <Box component="span" sx={{ color: 'text.secondary', mr: 1 }}>Totale udienze</Box>
                          {udienzeRows.length}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FileText size={16} color="#64748b" />
                        <Typography variant="body2">
                          <Box component="span" sx={{ color: 'text.secondary', mr: 1 }}>Ultima udienza</Box>
                          {udienzeRows.length ? formatDate(udienzeRows[udienzeRows.length - 1]?.data_udienza) : '-'}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            )}

            <Box
              sx={{
                mt: 2,
                display: 'flex',
                justifyContent: 'space-between',
                gap: 1,
                flexWrap: 'wrap',
                position: 'sticky',
                bottom: 0,
                zIndex: 5,
                bgcolor: 'background.paper',
                borderTop: '1px solid',
                borderColor: 'divider',
                py: 1.25,
                px: 1.25,
                borderRadius: 1.5,
                boxShadow: '0 -6px 16px rgba(15, 23, 42, 0.06)'
              }}
            >
              <Box>
                {formTab > 0 && (
                  <Button variant="outlined" onClick={() => setFormTab((prev) => Math.max(0, prev - 1))} disabled={saving}>
                    Indietro
                  </Button>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  onClick={closeFormDialog}
                  disabled={saving}
                >
                  Annulla
                </Button>
                {formTab < maxFormTab ? (
                  <Button
                    variant="contained"
                    onClick={() => setFormTab((prev) => Math.min(maxFormTab, prev + 1))}
                    disabled={saving}
                  >
                    Salva e continua
                  </Button>
                ) : (
                  <Button variant="contained" onClick={handleSave} disabled={saving}>
                    {saving ? 'Salvataggio...' : editingRow ? 'Salva' : 'Salva e crea'}
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        </Paper>
      </Dialog>

      <Dialog
        open={autoStatusInfoOpen}
        onClose={() => setAutoStatusInfoOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>Dettaglio Auto stato</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              mb: 1.5,
              p: 1.5,
              borderRadius: 2,
              bgcolor: '#eff6ff',
              border: '1px solid #bfdbfe'
            }}
          >
            <Typography variant="body2" sx={{ color: '#1e3a8a', fontWeight: 600 }}>
              Se attivo, il sistema aggiorna automaticamente lo stato della pratica in base ai dati disponibili.
            </Typography>
          </Box>
          <Box sx={{ display: 'grid', gap: 1.25 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Activity size={17} color="#2563eb" />
              <Typography variant="body2">
                In presenza di dati procedurali (es. numero R.G. o iscrizione a ruolo), udienze o allegati, viene proposto lo stato `In corso`.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <CheckCircle2 size={17} color="#16a34a" />
              <Typography variant="body2">
                Se la data chiusura è valorizzata, viene proposto lo stato `Definita`.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Archive size={17} color="#64748b" />
              <Typography variant="body2">
                Se imposti manualmente `Archiviata` o `Sospesa`, la scelta manuale viene mantenuta.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <PauseCircle size={17} color="#f59e0b" />
              <Typography variant="body2">
                Se disattivo, lo stato resta gestito manualmente.
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setAutoStatusInfoOpen(false)}>
            Ho capito
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={detailDialogOpen}
        onClose={() => {
          setDetailDialogOpen(false);
          setDetailRow(null);
          setDetailUdienze([]);
          setDetailLoading(false);
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Dettaglio pratica U/O</DialogTitle>
        <DialogContent dividers>
          {!detailRow ? (
            <Typography variant="body2" color="text.secondary">
              Nessun dettaglio disponibile.
            </Typography>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                    Informazioni principali
                  </Typography>
                  <Box sx={{ display: 'grid', gap: 0.75 }}>
                    <Typography variant="body2"><strong>Codice:</strong> {detailRow.codice_pratica || '-'}</Typography>
                    <Typography variant="body2"><strong>Cliente:</strong> {detailRow.cliente_nome || '-'}</Typography>
                    <Typography variant="body2"><strong>Controparte:</strong> {detailRow.controparte_nome || '-'}</Typography>
                    <Typography variant="body2">
                      <strong>Posizioni credito:</strong>{' '}
                      {Array.isArray(detailRow.tipi_posizione_credito_nomi) && detailRow.tipi_posizione_credito_nomi.length
                        ? detailRow.tipi_posizione_credito_nomi.join(', ')
                        : '-'}
                    </Typography>
                    <Typography variant="body2"><strong>Stato:</strong> {detailRow.stato_pratica || '-'}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2"><strong>Modalità stato:</strong></Typography>
                      <Chip
                        size="small"
                        label={getAutoStatusLabel(detailRow.stato_auto)}
                        sx={getAutoStatusChipSx(detailRow.stato_auto)}
                      />
                    </Box>
                    <Typography variant="body2"><strong>Tribunale:</strong> {detailRow.tribunale_nome || '-'}</Typography>
                    <Typography variant="body2"><strong>Iscrizione a ruolo:</strong> {formatDate(detailRow.data_iscrizione_ruolo)}</Typography>
                    <Typography variant="body2"><strong>Termine costituzione:</strong> {formatDate(detailRow.termine_costituzione)}</Typography>
                    <Typography variant="body2"><strong>Termine memorie:</strong> {formatDate(detailRow.termine_memorie)}</Typography>
                    <Typography variant="body2"><strong>Data scadenza:</strong> {formatDate(detailRow.data_scadenza)}</Typography>
                    <Typography variant="body2"><strong>Oggetto:</strong> {detailRow.oggetto || '-'}</Typography>
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                    Sintesi udienze
                  </Typography>
                  {detailLoading ? (
                    <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
                      <CircularProgress size={22} />
                    </Box>
                  ) : (
                    <Box sx={{ display: 'grid', gap: 0.75 }}>
                      <Typography variant="body2"><strong>Totale udienze:</strong> {detailUdienze.length}</Typography>
                      <Typography variant="body2">
                        <strong>Prossima udienza:</strong>{' '}
                        {(() => {
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          const next = detailUdienze
                            .filter((u) => {
                              const d = new Date(u.data_udienza);
                              d.setHours(0, 0, 0, 0);
                              return !Number.isNaN(d.getTime()) && d >= today;
                            })
                            .sort((a, b) => new Date(a.data_udienza) - new Date(b.data_udienza))[0];
                          if (!next) return '-';
                          return `${formatDate(next.data_udienza)}${next.ora_udienza ? ` ${String(next.ora_udienza).slice(0, 5)}` : ''}`;
                        })()}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Ultimo esito:</strong>{' '}
                        {(() => {
                          const last = [...detailUdienze]
                            .filter((u) => u.data_udienza)
                            .sort((a, b) => new Date(b.data_udienza) - new Date(a.data_udienza))[0];
                          return last?.esito_udienza_nome || last?.esito_libero || '-';
                        })()}
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </Grid>
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                    Elenco udienze
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>#</TableCell>
                          <TableCell>Data</TableCell>
                          <TableCell>Ora</TableCell>
                          <TableCell>Esito</TableCell>
                          <TableCell>Note</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {detailLoading ? (
                          <TableRow>
                            <TableCell colSpan={5} align="center">
                              <CircularProgress size={20} />
                            </TableCell>
                          </TableRow>
                        ) : detailUdienze.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={5} align="center">
                              <Typography variant="body2" color="text.secondary">
                                Nessuna udienza presente.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          detailUdienze.map((u) => (
                            <TableRow key={u.id}>
                              <TableCell>{u.numero_udienza}</TableCell>
                              <TableCell>{formatDate(u.data_udienza)}</TableCell>
                              <TableCell>{u.ora_udienza ? String(u.ora_udienza).slice(0, 5) : '-'}</TableCell>
                              <TableCell>{u.esito_udienza_nome || u.esito_libero || '-'}</TableCell>
                              <TableCell>{u.note || '-'}</TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDetailDialogOpen(false);
              setDetailRow(null);
              setDetailUdienze([]);
              setDetailLoading(false);
            }}
          >
            Chiudi
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={noteDeleteDialogOpen}
        onClose={() => {
          if (noteDeletingId) return;
          setNoteDeleteDialogOpen(false);
          setNoteDeleteTarget(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>Elimina nota</DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            Confermi l&apos;eliminazione definitiva della nota?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            La nota verra rimossa dalla timeline della pratica e non potra essere recuperata.
          </Typography>
          <Box
            sx={{
              p: 1.25,
              borderRadius: 1.5,
              border: '1px solid #fecaca',
              bgcolor: '#fef2f2'
            }}
          >
            <Typography variant="body2">
              <strong>Autore:</strong> {noteDeleteTarget?.autore_nome || '-'}
            </Typography>
            <Typography variant="body2">
              <strong>Data:</strong> {noteDeleteTarget?.created_at ? formatDateTime(noteDeleteTarget.created_at) : '-'}
            </Typography>
            <Typography variant="body2">
              <strong>Anteprima:</strong>{' '}
              {String(noteDeleteTarget?.testo || '-').slice(0, 140)}
              {String(noteDeleteTarget?.testo || '').length > 140 ? '...' : ''}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setNoteDeleteDialogOpen(false);
              setNoteDeleteTarget(null);
            }}
            disabled={Boolean(noteDeletingId)}
          >
            Annulla
          </Button>
          <Button color="error" variant="contained" onClick={handleDeleteNote} disabled={Boolean(noteDeletingId)}>
            {noteDeletingId ? 'Eliminazione...' : 'Elimina nota'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={tribunaleCreateDialogOpen}
        onClose={() => {
          if (creatingTribunale) return;
          setTribunaleCreateDialogOpen(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Nuovo tribunale</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                autoFocus
                label="Nome"
                value={newTribunale.nome}
                onChange={(e) => setNewTribunale((prev) => ({ ...prev, nome: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Città"
                value={newTribunale.citta}
                onChange={(e) => setNewTribunale((prev) => ({ ...prev, citta: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Provincia"
                inputProps={{ maxLength: 10 }}
                value={newTribunale.provincia}
                onChange={(e) =>
                  setNewTribunale((prev) => ({ ...prev, provincia: String(e.target.value || '').toUpperCase() }))
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Indirizzo"
                value={newTribunale.indirizzo}
                onChange={(e) => setNewTribunale((prev) => ({ ...prev, indirizzo: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTribunaleCreateDialogOpen(false)} disabled={creatingTribunale}>
            Annulla
          </Button>
          <Button variant="contained" onClick={handleCreateTribunale} disabled={creatingTribunale}>
            {creatingTribunale ? 'Salvataggio...' : 'Salva tribunale'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={udienzaDialogOpen}
        onClose={() => {
          if (udienzaSaving) return;
          setUdienzaDialogOpen(false);
          setEditingUdienza(null);
          resetUdienzaForm();
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{editingUdienza ? 'Modifica udienza' : 'Nuova udienza'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Numero udienza"
                value={udienzaForm.numero_udienza}
                onChange={(e) => setUdienzaForm((prev) => ({ ...prev, numero_udienza: e.target.value }))}
                helperText="Lascia vuoto per progressivo automatico"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                required
                type="date"
                label="Data udienza"
                value={udienzaForm.data_udienza}
                onChange={(e) => setUdienzaForm((prev) => ({ ...prev, data_udienza: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="time"
                label="Ora udienza"
                value={udienzaForm.ora_udienza}
                onChange={(e) => setUdienzaForm((prev) => ({ ...prev, ora_udienza: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Esito udienza</InputLabel>
                <Select
                  label="Esito udienza"
                  value={udienzaForm.esito_udienza_id}
                  onChange={(e) =>
                    setUdienzaForm((prev) => ({ ...prev, esito_udienza_id: e.target.value }))
                  }
                >
                  <MenuItem value="">
                    <em>Nessuno</em>
                  </MenuItem>
                  {udienzeEsitiOptions.map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.nome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Esito libero"
                value={udienzaForm.esito_libero}
                onChange={(e) => setUdienzaForm((prev) => ({ ...prev, esito_libero: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Note"
                value={udienzaForm.note}
                onChange={(e) => setUdienzaForm((prev) => ({ ...prev, note: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (udienzaSaving) return;
              setUdienzaDialogOpen(false);
              setEditingUdienza(null);
              resetUdienzaForm();
            }}
            disabled={udienzaSaving}
          >
            Annulla
          </Button>
          <Button variant="contained" onClick={handleSaveUdienza} disabled={udienzaSaving}>
            {udienzaSaving ? 'Salvataggio...' : 'Salva'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={udienzaDeleteDialogOpen}
        onClose={() => {
          if (udienzaDeleteLoading) return;
          setUdienzaDeleteDialogOpen(false);
          setUdienzaDeleteTarget(null);
        }}
      >
        <DialogTitle>Elimina udienza</DialogTitle>
        <DialogContent dividers>
          <Typography>
            Confermi l&apos;eliminazione dell&apos;udienza n.
            <strong>{udienzaDeleteTarget?.numero_udienza ? ` ${udienzaDeleteTarget.numero_udienza}` : ''}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setUdienzaDeleteDialogOpen(false);
              setUdienzaDeleteTarget(null);
            }}
            disabled={udienzaDeleteLoading}
          >
            Annulla
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteUdienza}
            disabled={udienzaDeleteLoading}
          >
            {udienzaDeleteLoading ? 'Eliminazione...' : 'Elimina'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          if (!deleteLoading) {
            setDeleteDialogOpen(false);
            setDeleteTarget(null);
          }
        }}
      >
        <DialogTitle>Elimina pratica U/O</DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            Confermi l&apos;eliminazione definitiva?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Questa operazione rimuove in modo permanente la pratica selezionata e non puo essere annullata.
          </Typography>
          <Box
            sx={{
              p: 1.25,
              borderRadius: 1.5,
              border: '1px solid #fecaca',
              bgcolor: '#fef2f2'
            }}
          >
            <Typography variant="body2">
              <strong>Cliente:</strong> {deleteTarget?.cliente_nome || '-'}
            </Typography>
            <Typography variant="body2">
              <strong>Controparte:</strong> {deleteTarget?.controparte_nome || '-'}
            </Typography>
            <Typography variant="body2">
              <strong>Stato:</strong> {deleteTarget?.stato_pratica || '-'}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setDeleteTarget(null);
            }}
            disabled={deleteLoading}
          >
            Annulla
          </Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleteLoading}>
            {deleteLoading ? 'Eliminazione...' : 'Elimina'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={reportDialogOpen} onClose={() => setReportDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Report Pratiche U/O</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {search.trim() ? (
              <Chip size="small" label={`Ricerca: ${search.trim()}`} />
            ) : (
              <Chip size="small" label="Nessuna ricerca" />
            )}
            {filterStatus ? <Chip size="small" label={`Stato: ${filterStatus}`} /> : <Chip size="small" label="Stato: tutti" />}
            {filterUrgente === 'urgenze' ? (
              <Chip size="small" label="Tipo: Urgenze" />
            ) : filterUrgente === 'opposizioni' ? (
              <Chip size="small" label="Tipo: Opposizioni" />
            ) : (
              <Chip size="small" label="Tipo: tutti" />
            )}
          </Box>
          <Box sx={{ mt: 2 }}>
            <FormControl component="fieldset">
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                Formato report
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={reportIncludeDettaglio}
                    onChange={(e) => setReportIncludeDettaglio(Boolean(e.target.checked))}
                  />
                }
                label="Includi dettaglio (cliente/controparte/referenti/udienze)"
              />
              <Typography variant="body2" color="text.secondary">
                Se disattivato, il PDF sarà sintetico e riporterà solo le colonne visibili in lista.
              </Typography>
            </FormControl>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            Il PDF verrà generato in background e sarà disponibile nella campanella notifiche.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportDialogOpen(false)} disabled={reportLoading}>
            Annulla
          </Button>
          <Button onClick={handleStartPraticheUoReport} variant="contained" disabled={reportLoading}>
            {reportLoading ? 'Avvio...' : 'Genera PDF'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
